import React from 'react';
import {
    Badge,
    Button,
    ButtonGroup,
    FormGroup,
    UncontrolledTooltip,
    Collapse,
    Container,
    Row,
    Col,
} from 'reactstrap';
import cls from 'classnames';
import { isEqual } from 'lodash-es';
import { v7 as uuid } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faFileContract,
    faPlus,
    faPlusCircle,
    faSave,
    faSort,
    faTasks,
    faTimes,
    faTrash,
    faSync,
    faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { Link } from '@s20/components/common/Link';
import { getInputFormat } from '@s20/common/util/date';
import { formatCurrency } from '@s20/common/util/number';
import { clone as cloneUtil } from '@s20/common/util/object';
import { email as emailUtil } from '@s20/common/util/validation';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexCenterRow,
    FlexEndRow,
    onFieldChange,
    onReactSelectChanged,
    SimpleTable,
    SmallButton,
    SubHeading,
    toasty,
    YesNoBadge,
    FormLabel,
    FormCheckbox,
    IconButton,
    Input,
    onYesNoChanged,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import { Can } from '../Can';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ApplicationPermissions,
    BillingCycles,
    CertifiedPayrollReporterForms,
    ChargeTypeResourceTypes,
    ServiceResponseResult,
    CustomFormContext,
} from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import NumericInput from '../common/forms/NumericInput';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ChargeTypeSelect from '../chargeType/ChargeTypeSelect';
import PayScheduleSelect from '../paySchedule/PayScheduleSelect';
import USCountyGroupSelect from '../uscounties/USCountyGroupSelect';
import ReorderListWidget from '../widgets/ReorderListWidget';
import SlideForm from '../common/forms/SlideForm';
import {
    ChargeTypeConfigurationType,
    _ContractChargeTypeEligibility,
} from '../chargeType/ChargeType';
import authService from '../api-authorization/AuthorizeService';
import EmailRecipients from '../common/forms/EmailRecipients';
import BillingRateForm from '../billing/BillingRateForm';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { Card } from '../common/Card';
import { TipAlert } from '../common/TipAlert';
import ChargeTypeGroupSelect from '../chargeTypeGroup/ChargeTypeGroupSelect';
import { _ChargeTypeGroupStatuses } from '../chargeTypeGroup/ChargeTypeGroup';
import { BillingRateTypes } from '../billing/BillingRate';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import { OrganizationContact } from '../organization/Organization';
import { Modal } from '../common/Modal';
import { Block } from '../common/Block';
import { ContractProjectProgressBar } from '../common/ContractProjectProgressBar';
import ContractHistoryForm from './ContractHistoryForm';
import { ContractPayrollIncentiveGrid } from './ContractPayrollIncentiveGrid';
import { EstimateChargeTypeGrid } from './EstimateChargeTypeGrid';
import { ContractBumpRuleGrid } from './ContractBumpRuleGrid';
import {
    Contract,
    WorkOrderNumberOptions,
    getWorkCardOptions,
    ChargeTypeEligbility,
    ChargeTypeEligibilities,
    _ContractStatuses,
    ContractChargeTypeBillingRate,
    ContractChargeTypeUSCountyGroupBillingRate,
    ContractChargeTypeGroupBillingRate,
    ContractChargeTypeJobTypeBillingRate,
    DayOfMonthSelect,
    DayOfWeekSelect,
    WarningThresholdOptions,
} from './Contract';

const getSalesOrderLineItems = async (salesOrderId) => await httpClient
    .js(ApiRoutes.estimate.lineItems(salesOrderId))
    .catch(() => {
        console.log('Unable to get charge type groups.');
    });

const formatDate = (dateString) => {
    const [month, day, year] = dateString.split('/');
    const [formattedDate] = new Date(`${year}-${month}-${day}`).toISOString().split('T');
    return formattedDate;
};

class ContractForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.chargeTypeSelectorRef = React.createRef();
        this.chargeTypeGroupSelectorRef = React.createRef();
        this.payScheduleSelectorRef = React.createRef();
        this.chargeTypesWidgetRef = React.createRef();
        this.rateFormRef = React.createRef();
        this.organizationContactFormRef = React.createRef();
        this.uSCountyGroupSelectorRef = React.createRef();

        const stateBase = {
            contract: new Contract(),
            contractStatuses: [],
            customerChargeTypes: [],
            dispatchLocations: [],
            operationsCenters: [],
            originalData: null,
            selectedChargeType: null,
            selectedJob: null,
            selectedSortableChargeTypes: [],
            showReorderWidget: false,
            workOrders: [],
            currentEmailAddress: null,
            billingCompanies: [],
            billingContacts: [],
            invoiceTerms: [],
            invoiceMappings: [],
            chargeTypeGroups: [],
            certifications: [],
            enableContractDateRange: false,
            expirationGracePeriodOptions: [],
            enableAutoEscalatingContractRates: false,
            enablePrevailingWageContracts: false,
            autoCalcDriveTimeBillingEnabled: false,
            contractUnionWorkTypes: [],
            contractScheduleTypes: [],
            usCountyGroups: [],
            usStates: [],
            isIntercompanyCustomer: false,
            showChangeContractNumberModal: false,
            newContractNumber: '',
            billingCycles: [],
            employeeGroups: [],
            jobTypes: [],
            showContactsForm: false,
            showContractHistory: false,
            selectedContact: null,
            contractTemplateLoading: false,
            customForms: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onYesNoChanged = this.onYesNoChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    // #region LIFECYCLE EVENTS
    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps
            && this.props.match.params.id !== (prevProps.match.params ?? {}).id
        ) {
            this.populateState();
        }
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    onViewChangeHistory = () => {
        this.setState({ showContractHistory: true });
    };

    onCloseContractHistory = () => {
        this.setState({ showContractHistory: false });
    };

    getActions = () => {
        const { contract } = this.state;

        if (!contract.id) {
            return null;
        }

        return [
            {
                key: 'view',
                onClick: this.onViewChangeHistory,
                icon: faTasks,
                label: 'View Change History',
                CanProps: {
                    I: 'history',
                    a: 'contract',
                },
            },
        ];
    };

    // #region BILLING CONTACTS
    onAddBillingContact = () => {
        const { contract } = { ...this.state };
        const selectedContact = {
            ...new OrganizationContact(),
            id: null,
            companyId:
                contract
                    .companyId,
            contactTypes: [2], // Forced as billing contact
        };

        this.setState({ showContactsForm: true, selectedContact });
    };

    onEditBillingContact = async (contactId, readOnly = false) => {
        const billingContact = await httpClient
            .js(ApiRoutes.locationContact.byId(contactId))
            .catch(() => {
                console.log('Unable to get billing contacts');
            });

        this.setState({
            showContactsForm: true,
            selectedContact: billingContact,
            contactFormIsReadOnly: readOnly,
        });
    };

    // #region CHARGE TYPES
    onAddChargeTypes = () => {
        const { contract } = { ...this.state };
        const { contractChargeTypes } = { ...contract };
        const typesToOmit = (contractChargeTypes ?? []).map((c) => c.chargeTypeId) ?? [];
        // pass in types already selected
        this.chargeTypeSelectorRef.current.open(null, [...typesToOmit], contract.isProjectContract);
    };

    onAddChargeTypeGroups = () => {
        const { contract } = { ...this.state };
        const { contractChargeTypeGroups } = { ...contract };
        const typesToOmit = (contractChargeTypeGroups ?? []).map((c) => c.chargeTypeGroupId) ?? [];
        // pass in types already selected
        this.chargeTypeGroupSelectorRef.current.open([...typesToOmit], contract.isProjectContract);
    };

    //on Refresh Charge Types from NetSuite
    onRefreshChargeTypes = async () => {
        const { contract } = { ...this.state };
        this.setState({ saving: true });

        const chargeTypes = await getSalesOrderLineItems(contract.createdFromId);
        const newChargeTypes = chargeTypes.filter((chargeType) => {
            const existing = contract.contractChargeTypes.find(
                (cct) => cct.createdFromId === chargeType.uniquekey,
            );

            if (existing) {
                existing.description = chargeType.memo;
                existing.customDescription = chargeType.memo;

                if (existing.billingRates?.[0]?.billingRate) {
                    existing.billingRates[0].billingRate.rate = chargeType.rate;
                }
                return false;
            }
            return true;
        });

        this.setState({
            saving: false,
            contract: {
                ...contract,
                contractNewChargeTypes: newChargeTypes,
            },
        });
    };

    onOrganizationContactSaved = async () => {
        this.setState({ contactFormIsReadOnly: true });

        const {
            selectedContact,
            billingContacts,
            contract,
        } = {
            ...this.state,
        };

        const isNew = parseInt(selectedContact.id ?? 0) <= 0;
        const result = await httpClient.andGetResponse(
            isNew ? httpClient.types.post : httpClient.types.put,
            isNew
                ? ApiRoutes.locationContact.create()
                : ApiRoutes.locationContact.update(selectedContact.id),
            { ...selectedContact },
            'Error Saving Contact',
        );

        if (result) {
            toasty.success('Contact saved.');

            if (selectedContact.contactTypes?.includes(2) && isNew) {
                billingContacts.push({
                    label: `${selectedContact.firstName} ${selectedContact.lastName}`,
                    value: result,
                });
            } else {
                const existingContactIndex = billingContacts.findIndex((c) => c.value === selectedContact.id);
                if (existingContactIndex > -1) {
                    billingContacts[existingContactIndex].label = `${selectedContact.firstName} ${selectedContact.lastName}`;
                }
            }

            this.setState({
                showContactsForm: false,
                saving: false,
                billingContacts,
                contract: { ...contract, billingContactId: isNew ? result : selectedContact.id },
            });
        } else {
            this.setState({ saving: false });
        }

        this.setState({ contactFormIsReadOnly: false });
        this.organizationContactFormRef.current.resetForm();
        this.setState({ selectedContact: null });
    };
    // #endregion
    onAddChargeTypesCallback = (selections) => {
        const { contract } = { ...this.state };
        let updated = [...contract.contractChargeTypes] ?? [];

        // get selections from the popup
        const selectedChargeTypes = [...selections].map((x) => {
            let chargeTypeEligibilityId = null;

            if (x.resourceTypeId == 5) {
                chargeTypeEligibilityId = !x.isSurcharge ? 2 : 1;
            }

            return {
                order: null /* append to the end, user can then order them */,
                contractTemplateId: contract.id,
                chargeTypeId: x.id,
                chargeTypeEligibilityId,
                description: x.description,
                chargeUnits: x.unitsName,
                group: x.appliesToGroup,
                employees: x.appliesToUsers,
                equipment: x.appliesToEquipment,
                billable: x.isBillable,
                resourceTypeId: x.resourceTypeId,
                isSurcharge: x.isSurcharge,
                isPermit: x.isPermit,
            };
        });

        // merge additions with current charges
        updated = updated.concat([...selectedChargeTypes]);

        // reorder everything after the add
        updated = updated.map((x, index) => ({ ...x, ...{ order: (index += 1) } }));

        contract.contractChargeTypes = [...updated];

        this.setState({ contract: { ...contract } });
    };

    onAddChargeTypeGroupsCallback = (selections) => {
        const { contract } = { ...this.state };
        let updated = [...contract.contractChargeTypeGroups] ?? [];

        // get selections from the popup
        const selectedChargeTypeGroups = [...selections].map((x) => ({
            contractTemplateId: contract.id,
            chargeTypeGroupId: x.value,
            invoiceDescription: x.label,
            chargeTypes: x.chargeTypes,
            chargeTypeBillingRates: [],
            billingRates: [],
        }));

        // merge additions with current charges
        updated = updated.concat([...selectedChargeTypeGroups]);

        contract.contractChargeTypeGroups = [...updated];

        this.setState({ contract: { ...contract } });
    };

    onAddJob = () => {
        this.context.history.push(
            `${AppNavPaths.JobNew}/${this.props.match.params.id}`,
        );
    };

    onAddProject = () => {
        this.context.history.push(
            `${AppNavPaths.ProjectNew}/${this.props.match.params.id}`,
        );
    };

    onAddPayScheduleCallback = (selections) => {
        const { contract } = { ...this.state };
        let updated = [...contract.contractPaySchedules] ?? [];

        // get selections from the popup
        const selectedPaySchedules = [...selections].map((x) => ({
            id: 0,
            contractId: contract.id,
            payScheduleId: x.id,
            paySchedule: {
                id: x.id,
                governingBody: x.governingBody,
                state: x.state,
                counties: x.counties,
                reg: x.reg,
                ot: x.ot,
                fringe: x.fringe,
            },
        }));

        const contractHasStateOnlyPS = contract.contractPaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        const selectionsStateOnlyPS = selectedPaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        const contractHasItems = contract.contractPaySchedules != null
            && contract.contractPaySchedules.length > 0;

        // Contract has a state only pay schedule, nothing else can be added
        if (contractHasStateOnlyPS?.length > 0) {
            toasty.error(
                'Pay Schedule Conflict',
                'There is a pay schedule for the entire state. No more pay schedules can be added.',
            );
            return;
        }

        // Selections contain a state only pay schedule, but contract already has a pay schedule
        if (selectionsStateOnlyPS.length > 0 && contractHasItems) {
            toasty.error(
                'Pay Schedule Conflict',
                'Unable to add pay schedule for the entire state due to an existing pay schedule.',
            );
            return;
        }

        // Selections contain a state only pay schedule, with multiple selections
        if (
            selectionsStateOnlyPS.length > 0
            && selectedPaySchedules.length > 1
        ) {
            toasty.error(
                'Pay Schedule Conflict',
                'Cannot add other pay schedules with a pay schedule for the entire state.',
            );
            return;
        }

        // merge additions with current charges
        updated = updated.concat([...selectedPaySchedules]);
        contract.contractPaySchedules = [...updated];

        this.setState({ contract: { ...contract } });
    };

    // #endregion

    onAddPaySchedules = () => {
        const { contract } = { ...this.state };
        const { contractPaySchedules } = contract;

        const paySchedulesNoCounties = contract.contractPaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        if (paySchedulesNoCounties?.length > 0) {
            toasty.error(
                'Pay Schedule Conflict',
                'There is a pay schedule for the entire state. No more pay schedules can be added.',
            );
            return;
        }

        const itemsToOmit = (contractPaySchedules ?? []).map((c) => c.payScheduleId) ?? [];

        this.payScheduleSelectorRef.current.open(null, [...itemsToOmit]);
    };

    onAddRate = (chargeType) => {
        const selectedRate = { ...new ContractChargeTypeBillingRate() };
        selectedRate.chargeTypeId = chargeType.chargeTypeId;
        selectedRate.chargeTypeName = chargeType.description;
        selectedRate.contractId = chargeType.contractId;

        selectedRate.usCountyGroupBillingRates = this.getUSCountyGroups(chargeType); // Union work Billing Rates
        selectedRate.jobTypeBillingRates = this.getJobTypeRates(chargeType); // Job Types Billing Rates

        this.rateFormRef.current.resetForm(); // clear validation
        this.setState((state) => {
            state.selectedChargeType = { ...chargeType, rateType: BillingRateTypes.CHARGE_TYPE };
            state.selectedRate = selectedRate;
            state.showRateForm = true;
            return state;
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onAddGroupRate = (chargeTypeGroup) => {
        const selectedRate = { ...new ContractChargeTypeGroupBillingRate() };
        selectedRate.chargeTypeGroupId = chargeTypeGroup.chargeTypeGroupId;
        selectedRate.chargeTypeName = chargeTypeGroup.invoiceDescription;
        selectedRate.contractId = chargeTypeGroup.contractId;

        selectedRate.usCountyGroupBillingRates = this.getUSCountyGroups(chargeTypeGroup); // Union work Billing Rates
        selectedRate.jobTypeBillingRates = this.getJobTypeRates(chargeTypeGroup); // Job Types Billing Rates

        this.rateFormRef.current.resetForm(); // clear validation
        this.setState((state) => {
            state.selectedChargeType = { ...chargeTypeGroup, rateType: BillingRateTypes.CHARGE_TYPE_GROUP };
            state.selectedRate = selectedRate;
            state.showRateForm = true;
            return state;
        });
        this.context.setFormOpened(true); // lock all other page fields while form is open
    };

    onAllowNonFlaggingChanged = (value) => {
        const { contract } = { ...this.state };
        contract.enableNonFlaggingTimesheets = value;
        // FOR NOW, PER CW:
        // Hide the allow self-dispatch button (since self-dispatch isn't yet finished)
        // and default to true when the non-flag contract is set up.
        contract.allowSelfDispatch = !!value;
        this.setState({ contract });
    };

    onAutoCalculateDriveTimeBillingChanged = (value) => {
        const { contract } = { ...this.state };
        contract.autoCalculateDriveTimeBilling = value;
        this.setState({ contract });
    };

    // #endregion

    // #region BASE FORM EVENTS
    onChange = onFieldChange;

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };


    /**
     * When the customer selection changes, populate customer specific inputs.
     *
     * @param {any} customer
     */
    onCustomerChanged = async (customer) => {
        const { contract } = this.state;
        const companyId = (customer ?? {}).value ?? null;

        this.setState({
            contract,
            selectedContact: null,
        });

        contract.companyId = companyId;

        if (companyId) {
            const [
                centers,
                chargeTypes,
                allowAlternativePayroll,
                operationsCentersApply,
                defaultTerms,
                defaultMapping,
                isIntercompanyCustomer,
            ] = await Promise.all([
                httpClient.js(
                    ApiRoutes.company.customerOperationsCentersCompanies(
                        companyId,
                    ),
                ),
                httpClient
                    .js(ApiRoutes.chargeType.byCustomer(companyId))
                    .catch((err) => {
                        console.log(
                            `Failed to get charge types for selected customer: ${err.toString()}`,
                        );
                    }),
                httpClient.js(
                    ApiRoutes.company.allowAlternativePayroll(companyId),
                ),
                httpClient.js(
                    ApiRoutes.company.operationsCentersApply(companyId),
                ),
                httpClient
                    .js(ApiRoutes.company.defaultTerms(companyId))
                    .catch(() => {
                        console.log('No terms setup for selected customer}');
                    }),
                httpClient
                    .js(ApiRoutes.company.defaultMapping(companyId))
                    .catch(() => {
                        console.log('No mapping setup for selected customer}');
                    }),
                httpClient.js(
                    ApiRoutes.company.isIntercompany(companyId),
                ),
            ]);

            contract.invoiceTermsId = defaultTerms;
            contract.invoiceMappingId = defaultMapping;
            contract.chargeTypes = chargeTypes;
            contract.hasAlternativePayroll = allowAlternativePayroll;
            contract.operationsCentersApply = operationsCentersApply;
            contract.billingContactId = null;

            let billingContacts = [];

            if (contract.companyId) {
                const [contacts] = await Promise.all([
                    httpClient
                        .js(
                            ApiRoutes.typeAheads.billingContacts(
                                contract.companyId,
                            ),
                        )
                        .catch(() => console.log('Unable to get the billing contacts.')),
                ]);

                if (contacts) {
                    billingContacts = contacts.map((x) => ({ label: x.label, value: x.value }));
                }
            }

            this.setState({
                contract,
                operationsCenters: centers.map((x) => ({ label: x.companyName, value: x.id })),
                billingContacts,
                customerChargeTypes: chargeTypes,
                isIntercompanyCustomer,
            });
        } else {
            contract.invoiceTermsId = null;
            contract.invoiceMappingId = null;
            contract.chargeTypes = [];
            contract.hasAlternativePayroll = false;
            contract.operationsCentersApply = false;
            contract.billingContactId = null;

            this.setState({
                contract,
                operationsCenters: [],
                billingContacts: [],
                customerChargeTypes: [],
                isIntercompanyCustomer: false,
            });
        }
    };

    onDelete = async () => {
        const { contract } = this.state;
        const response = await httpClient.delete(
            ApiRoutes.contract.delete(contract.id),
        );
        if (response) this.onClose(response);
    };
    // #endregion

    onEditRate = (chargeType) => {
        const selectedWorkType = this.getContractUnionWorkType();
        const rate = selectedWorkType?.isUnionWork && chargeType.usCountyGroupBillingRates.length > 0 ? chargeType.usCountyGroupBillingRates[0] : chargeType.billingRates[0];

        const selectedRate = { ...new ContractChargeTypeBillingRate() };

        const cloned = cloneUtil(rate);
        selectedRate.id = cloned.id;
        selectedRate.billingRate = cloned.billingRate;
        selectedRate.billingRateId = cloned.billingRateId;
        selectedRate.chargeTypeId = chargeType.chargeTypeId;
        selectedRate.chargeTypeName = chargeType.description;
        selectedRate.contractId = chargeType.contractId;

        selectedRate.usCountyGroupBillingRates = this.getUSCountyGroups(chargeType, true, chargeType.usCountyGroupBillingRates); // Union work Billing Rates
        selectedRate.jobTypeBillingRates = this.getJobTypeRates(chargeType, true, chargeType.jobTypeBillingRates); // Job Types Billing Rates

        this.rateFormRef.current.resetForm();
        this.setState({
            selectedChargeType: { ...chargeType, rateType: BillingRateTypes.CHARGE_TYPE },
            selectedRate,
            showRateForm: true,
            showRateFormDelete: true,
        });
        this.context.setFormOpened(true);
    };

    onEditGroupRate = (chargeTypeGroup) => {
        const selectedWorkType = this.getContractUnionWorkType();
        const rate = selectedWorkType?.isUnionWork && chargeTypeGroup.usCountyGroupBillingRates.length > 0 ?
            chargeTypeGroup.usCountyGroupBillingRates[0] :
            chargeTypeGroup.billingRates[0];

        const selectedRate = { ...new ContractChargeTypeGroupBillingRate() };

        const cloned = cloneUtil(rate);
        selectedRate.id = cloned.id;
        selectedRate.billingRate = cloned.billingRate;
        selectedRate.billingRateId = cloned.billingRateId;
        selectedRate.chargeTypeGroupId = chargeTypeGroup.chargeTypeGroupId;
        selectedRate.chargeTypeName = chargeTypeGroup.invoiceDescription;
        selectedRate.contractId = chargeTypeGroup.contractId;

        selectedRate.usCountyGroupBillingRates = this.getUSCountyGroups(chargeTypeGroup, true, chargeTypeGroup.usCountyGroupBillingRates); // Union work Billing Rates
        selectedRate.jobTypeBillingRates = this.getJobTypeRates(chargeTypeGroup, true, chargeTypeGroup.jobTypeBillingRates); // Job Types Billing Rates

        this.rateFormRef.current.resetForm();
        this.setState({
            selectedChargeType: { ...chargeTypeGroup, rateType: BillingRateTypes.CHARGE_TYPE_GROUP },
            selectedRate,
            showRateForm: true,
            showRateFormDelete: true,
        });
        this.context.setFormOpened(true);
    };

    // #endregion

    // #region EMAIL

    onEmailAdded = () => {
        const { currentEmailAddress, contract } = { ...this.state };
        if (currentEmailAddress) {
            const emailAddress = currentEmailAddress.trim();
            const match = emailUtil(emailAddress);
            if (match) {
                if (!(contract.timesheetEmailRecipients ?? []).length) contract.timesheetEmailRecipients = [];
                if (!contract.timesheetEmailRecipients.includes(emailAddress)) contract.timesheetEmailRecipients.push(emailAddress);
                this.setState({
                    contract: { ...contract },
                    currentEmailAddress: null,
                });
            } else {
                toasty.error('Please enter a single valid email address.');
            }
        }
    };

    onEmailChanged = (e) => {
        const val = e.currentTarget.value;
        this.setState({ currentEmailAddress: val });
    };

    onEmailRemoved = (selected) => {
        const { contract } = { ...this.state };
        const inx = contract.timesheetEmailRecipients.findIndex(
            (ea) => ea === selected,
        );
        contract.timesheetEmailRecipients.splice(inx, 1);
        this.setState({ contract });
    };

    // #endregion
    onRateFormClosed = () => {
        this.rateFormRef.current.resetForm(); // clear validation
        this.context.setFormOpened(false); // lock all other page fields while form is open
        this.setState({
            showRateForm: false,
            showRateFormDelete: false,
        });
    };

    onRateRemoved = () => {
        const { contract, selectedChargeType } = this.state;

        let chargeType = contract.contractChargeTypes.find(
            (x) => x.chargeTypeId == selectedChargeType.chargeTypeId,
        );

        if (!chargeType) {
            chargeType = contract.contractChargeTypeGroups.find(
                (x) => x.chargeTypeGroupId === selectedChargeType.chargeTypeGroupId,
            );
        }

        chargeType.billingRates = [];
        chargeType.usCountyGroupBillingRates = [];

        this.rateFormRef.current.resetForm();
        this.setState({
            contract,
            showRateForm: false,
        });
    };

    onRateSaved = () => {
        const { contract, selectedRate, selectedChargeType } = this.state;

        const chargeType = selectedChargeType.rateType === BillingRateTypes.CHARGE_TYPE ?
            contract.contractChargeTypes.find(
                (x) => x.chargeTypeId == selectedChargeType.chargeTypeId,
            ) :
            contract.contractChargeTypeGroups.find(
                (x) => x.chargeTypeGroupId == selectedChargeType.chargeTypeGroupId,
            );

        const selectedWorkType = this.getContractUnionWorkType();

        const usCountyGroupBillingRates = Object.values(selectedRate.usCountyGroupBillingRates || {});

        if (selectedWorkType?.isUnionWork && usCountyGroupBillingRates.length > 0) {
            chargeType.usCountyGroupBillingRates = usCountyGroupBillingRates.map((rate) => {
                if (rate.id) {
                    return {
                        ...rate,
                        billingRate: {
                            ...rate.billingRate,
                            altRateDescription: selectedRate.billingRate.altRateDescription,
                            altRateQBInventoryItemId: selectedRate.billingRate.altRateQBInventoryItemId,
                            effectiveDate: selectedRate.billingRate.effectiveDate,
                            expirationDate: selectedRate.billingRate.expirationDate,
                            otRateDescription: selectedRate.billingRate.otRateDescription,
                            otRateQBInventoryItemId: selectedRate.billingRate.otRateQBInventoryItemId,
                            rateDescription: selectedRate.billingRate.rateDescription,
                            rateQBInventoryItemId: selectedRate.billingRate.rateQBInventoryItemId,
                            sdRateDescription: selectedRate.billingRate.sdRateDescription,
                            sdRateQBInventoryItemId: selectedRate.billingRate.sdRateQBInventoryItemId,
                            rentalUnitTypeId: selectedRate.billingRate.rentalUnitTypeId,
                            isTaxable: selectedRate.billingRate.isTaxable,
                        },
                    };
                }

                return {
                    billingRate: {
                        ...rate.billingRate,
                        altRateDescription: selectedRate.billingRate.altRateDescription,
                        altRateQBInventoryItemId: selectedRate.billingRate.altRateQBInventoryItemId,
                        effectiveDate: selectedRate.billingRate.effectiveDate,
                        expirationDate: selectedRate.billingRate.expirationDate,
                        otRateDescription: selectedRate.billingRate.otRateDescription,
                        otRateQBInventoryItemId: selectedRate.billingRate.otRateQBInventoryItemId,
                        sdRateDescription: selectedRate.billingRate.sdRateDescription,
                        sdRateQBInventoryItemId: selectedRate.billingRate.sdRateQBInventoryItemId,
                        rateDescription: selectedRate.billingRate.rateDescription,
                        rateQBInventoryItemId: selectedRate.billingRate.rateQBInventoryItemId,
                        rentalUnitTypeId: selectedRate.billingRate.rentalUnitTypeId,
                        isTaxable: selectedRate.billingRate.isTaxable,
                        id: 0,
                    },
                    billingRateId: 0,
                    contractBillingRateId: 0,
                    usCountyGroupId: rate.usCountyGroupId,
                };
            }).filter((rate) => rate.billingRate.current); // Filter out billing rates without values
        } else {
            if (!chargeType.billingRates || chargeType.billingRates.length === 0) {
                selectedRate.billingRate.id = 0;
                chargeType.billingRates = [{
                    billingRate: selectedRate.billingRate,
                    billingRateId: 0,
                    contractBillingRateId: 0,
                }];
            } else {
                chargeType.billingRates[0].billingRate = selectedRate.billingRate;
            }

            const jobTypeBillingRates = Object.values(selectedRate.jobTypeBillingRates || {});
            if (jobTypeBillingRates?.length > 0) {
                chargeType.jobTypeBillingRates = jobTypeBillingRates.map((rate) => {
                    if (rate.id) {
                        return {
                            ...rate,
                            billingRate: {
                                ...rate.billingRate,
                                altRateDescription: selectedRate.billingRate.altRateDescription,
                                altRateQBInventoryItemId: selectedRate.billingRate.altRateQBInventoryItemId,
                                effectiveDate: selectedRate.billingRate.effectiveDate,
                                expirationDate: selectedRate.billingRate.expirationDate,
                                otRateDescription: selectedRate.billingRate.otRateDescription,
                                otRateQBInventoryItemId: selectedRate.billingRate.otRateQBInventoryItemId,
                                rateDescription: selectedRate.billingRate.rateDescription,
                                rateQBInventoryItemId: selectedRate.billingRate.rateQBInventoryItemId,
                                sdRateDescription: selectedRate.billingRate.sdRateDescription,
                                sdRateQBInventoryItemId: selectedRate.billingRate.sdRateQBInventoryItemId,
                                rentalUnitTypeId: selectedRate.billingRate.rentalUnitTypeId,
                                isTaxable: selectedRate.billingRate.isTaxable,
                            },
                        };
                    }

                    return {
                        billingRate: {
                            ...rate.billingRate,
                            altRateDescription: selectedRate.billingRate.altRateDescription,
                            altRateQBInventoryItemId: selectedRate.billingRate.altRateQBInventoryItemId,
                            effectiveDate: selectedRate.billingRate.effectiveDate,
                            expirationDate: selectedRate.billingRate.expirationDate,
                            otRateDescription: selectedRate.billingRate.otRateDescription,
                            otRateQBInventoryItemId: selectedRate.billingRate.otRateQBInventoryItemId,
                            sdRateDescription: selectedRate.billingRate.sdRateDescription,
                            sdRateQBInventoryItemId: selectedRate.billingRate.sdRateQBInventoryItemId,
                            rateDescription: selectedRate.billingRate.rateDescription,
                            rateQBInventoryItemId: selectedRate.billingRate.rateQBInventoryItemId,
                            rentalUnitTypeId: selectedRate.billingRate.rentalUnitTypeId,
                            isTaxable: selectedRate.billingRate.isTaxable,
                            id: 0,
                        },
                        billingRateId: 0,
                        contractBillingRateId: 0,
                        jobTypeId: rate.jobTypeId,
                    };
                }).filter((rate) => rate.billingRate.current);
            }
        }

        this.rateFormRef.current.resetForm();
        this.setState({
            showRateForm: false,
            contract,
        });
    };

    onRemoveCTGroup = (chargeTypeGroupId) => {
        const { contract } = { ...this.state };

        contract.contractChargeTypeGroups = contract.contractChargeTypeGroups.filter((x) => x.chargeTypeGroupId !== chargeTypeGroupId);

        this.setState({ contract: { ...contract } });
    };

    onRemoveChargeType = (chargeTypeId) => {
        const { contract } = { ...this.state };

        contract.contractChargeTypes = contract.contractChargeTypes.filter((x) => x.chargeTypeId !== chargeTypeId);

        this.setState({ contract: { ...contract } });
    };


    onRemovePaySchedule = (payScheduleIndex) => {
        const { contract } = { ...this.state };

        const paySchedules = [...contract.contractPaySchedules];
        paySchedules.splice(payScheduleIndex, 1);
        contract.contractPaySchedules = [...paySchedules];

        this.setState({ contract: { ...contract } });
    };

    onReorderChargeTypes = () => {
        // clone the list of charge types, to avoid mutating state.
        // pass to the widget.
        this.setState((state) => {
            const { contract } = { ...state };
            const selectedSortableChargeTypes = contract.contractChargeTypes;
            return {
                showReorderWidget: true,
                selectedSortableChargeTypes,
            };
        });
    };

    onSelectChanged = onReactSelectChanged;
    onYesNoChanged = onYesNoChanged;

    onCertifiedPayrollFormChanged = (selection, meta) => {
        this.onSelectChanged(selection, meta);

        if (Object.values(CertifiedPayrollReporterForms).includes(selection?.value)) {
            this.setState((state) => ({
                contract: { ...state.contract, requireFullSSN: true },
            }));
        }
    };

    onContactSelectChanged = (value) => {
        const { contract } = { ...this.state };
        contract.billingContactId = value?.value ?? null;
        this.setState({ contract });
    };

    onSubmit = async () => {
        const { contract } = { ...this.state };
        contract.autoCalculateDriveTimeBilling = Boolean(contract.autoCalculateDriveTimeBilling);

        const qrTemplateId = this.context?.tenant?.tenantSettings?.quickRentalContractTemplateId;
        if (qrTemplateId
            && contract.contractTemplateId === qrTemplateId
            && contract.number
            && !contract.number.startsWith('QR-')) {
            contract.number = `QR-${contract.number.replace(/^QR/, '')}`;
        }

        // if (
        //    contract.prevailingWage == true &&
        //    contract.contractPaySchedules.length == 0
        // ) {
        //    toasty.error(
        //        'Prevailing wage contracts must have at least one pay schedule assigned.'
        //    );
        //    return;
        // }

        // Clear any fluent api errors
        this.setState(() => ({ errors: {}, saving: true }));

        contract.contractChargeTypes = contract.contractChargeTypes.map((ct) => ({
            ...ct,
            billingRates: [...ct.billingRates || [],
            ...Object.values(ct.usCountyGroupBillingRates || []),
            ...Object.values(ct.jobTypeBillingRates || []),
            ],
            usCountyGroupBillingRates: null,
            jobTypeBillingRates: null,
        }));

        contract.contractChargeTypeGroups = contract.contractChargeTypeGroups.map((ct) => ({
            ...ct,
            billingRates: [...ct.billingRates || [],
            ...Object.values(ct.usCountyGroupBillingRates || []),
            ...Object.values(ct.jobTypeBillingRates || []),
            ],
            usCountyGroupBillingRates: null,
            jobTypeBillingRates: null,
            chargeTypes: null,
        }));

        contract.bumpRules = (this.state.contract.bumpRules || []).map((rule) => ({
            ...rule,
            id: typeof rule.id === 'string' ? 0 : rule.id,
        }));

        contract.payrollIncentives = (this.state.contract.payrollIncentives || []).map((incentive) => ({
            ...incentive,
            id: typeof incentive.id === 'string' ? 0 : (incentive.id ?? 0),
        }));

        if (!contract.id) {
            await this.create(contract);
        } else {
            await this.update(contract);
        }
    };

    handleIncentivesChange = (updatedIncentives) => {
        const { contract } = this.state;
        contract.payrollIncentives = updatedIncentives;

        this.setState({ contract });
    };

    create = async (contract) => {
        const response = await httpClient.andGetResponse(
            httpClient.types.post,
            ApiRoutes.contract.create(),
            { ...contract },
            'Error Creating Contract',
        );

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success('Contract created.');
            this.context.history.push(`${AppNavPaths.Contract}/${response.data}`);
        } else {
            toasty.error('Problem saving the contract:', response.message, {
                autoClose: false,
                closeOnClick: true,
            });
        }

        this.setState({ saving: false });
    };

    refreshChargeTypes = async (contractId) => {
        const response = await httpClient.js(ApiRoutes.contract.refreshChargeTypes(contractId));
        if (response.result === ServiceResponseResult.Ok) {
            toasty.success('Contract Charge Types updated.');

            this.context.history.go(0);
        } else {
            toasty.error('Problem refreshing contract charge types:', response.message, {
                autoClose: false,
                closeOnClick: true,
            });
        }
        this.setState({ saving: false });
    };

    // #endregion

    // #region SUBMISSION
    handleSaveError = handleFormSaveError;

    async newSalesOrderContract(contract, salesOrder, customers, billingCompanies) {
        const showWorkCardOnTimesheet = Boolean(
            this.context.tenant?.tenantSettings?.showWorkCardOnTimesheet,
        );

        const customer = customers?.find((c) => c.externalId == salesOrder.customer_id + '');
        const billing = billingCompanies?.find((c) => c.subsidiaryExternalId == salesOrder.subsidiary + '');

        const projectName = salesOrder.project_name ?? '';
        const prevailingWage = salesOrder.wage_scale == '1';
        contract.createdFromId = salesOrder.id;
        contract.estimateId = salesOrder.estimate_id;
        contract.companyId = customer?.id;
        contract.companyName = customer?.companyName;
        contract.billingCompanyId = billing?.value;
        contract.number = `${salesOrder?.estimate_number}${prevailingWage ? 'PW' : ''}`;
        contract.description = `${salesOrder?.so_number} - ${projectName}${prevailingWage ? ' PW' : ''}`;
        contract.prevailingWage = prevailingWage;
        contract.workOrderTypeId = salesOrder?.ecms_number || salesOrder?.prevailing_wage_pn ? 2 : 3;
        contract.effectiveDate = salesOrder?.estimate_date ? formatDate(salesOrder.estimate_date) : null;
        contract.expirationDate = salesOrder?.end_date ? formatDate(salesOrder.end_date) : null;
        contract.warningThresholdInDays = 30;
        contract.isProjectContract = salesOrder?.is_rental == '1';
        contract.billingCycleId = salesOrder?.billing_cycle_id;
        contract.ecmsNumber = salesOrder?.ecms_number;
        contract.pwProjectNumber = salesOrder?.prevailing_wage_pn;
        contract.workCardTypeId = showWorkCardOnTimesheet ? 4 : null;

        const chargeTypes = await getSalesOrderLineItems(salesOrder.id);
        contract.contractNewChargeTypes = (chargeTypes ?? []).map((ct) => ({ ...ct, isTaxable: !!ct.taxable }));
        contract.setupChargesRequired = !contract.isProjectContract && contract.contractNewChargeTypes.some((ct) => ct.resourcetypeid === ChargeTypeResourceTypes.Setup);

        const newPaySchedule = {
            governingBody: salesOrder.pay_schedule_name,
            state: salesOrder.pay_schedule_state,
            Counties: salesOrder.pay_schedule_counties,
            reg: parseFloat(salesOrder.pay_schedule_reg ?? '0'),
            ot: parseFloat(salesOrder.pay_schedule_ot ?? '0'),
            fringe: parseFloat(salesOrder.pay_schedule_fringe ?? '0'),
            useDailyOT: salesOrder.pay_schedule_dailyovertime == 'T',
            dailyOTThreshold: parseFloat(salesOrder.pay_schedule_dailyovertimethreshold ?? '0'),
        };
        contract.newPaySchedule = newPaySchedule;

        return contract;
    }

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const contract_id = this.props.match.params.id;
            const salesOrder = this.props.location.state?.salesOrder || {};

            // RLC: A note on this: This will not always be available because React's context is async.
            // It also bears no events for when it is ready, either.
            const { user } = this.context;

            // RLC: This is ultimately unused in the service, and probably should be cleaned up.
            // All tenant customers are returned.
            const companyId = (user ?? {}).companyId ?? 1;
            let chargeTypeGroups = [];

            const [
                customers,
                dispatchLocations,
                contractStatuses,
                contract,
                contractTemplates,
                billingCompanies,
                invoiceTerms,
                invoiceMappings,
                chargeTypes,
                contractUnionWorkTypes,
                contractScheduleTypes,
                usCountyGroups,
                billingCycles,
                resourceTypes,
                equipmentTypes,
                rentalUnitTypes,
                resGroups,
                certifications,
                employeeGroups,
                jobTypes,
                operationalCategories,
                usStates,
                cpForms,
            ] = await Promise.all([
                httpClient
                    .js(ApiRoutes.company.customerCompanies(companyId))
                    .catch(() => console.log('Unable to get customers.')),
                httpClient
                    .js(ApiRoutes.typeAheads.dispatchCompaniesForContract(contract_id))
                    .catch(() => console.log('Unable to get dispatch dispatchLocations.')),
                httpClient
                    .js(ApiRoutes.typeAheads.contractStatuses())
                    .catch(() => console.log('Unable to get contract statuses.')),
                contract_id
                    ? httpClient
                        .js(ApiRoutes.contract.byId(contract_id))
                        .catch(() => console.log('Unable to get contract.'))
                    : new Contract(),
                httpClient.js(ApiRoutes.contractTemplate.names()),
                httpClient
                    .js(ApiRoutes.typeAheads.billingCompanies())
                    .catch(() => console.log('Unable to get billing companies.')),
                httpClient
                    .js(ApiRoutes.typeAheads.invoiceTerms())
                    .catch(() => console.log('Unable to get payment terms.')),
                httpClient
                    .js(ApiRoutes.typeAheads.invoiceMapping())
                    .catch(() => console.log('Unable to get contract mapping.')),
                httpClient
                    .js(ApiRoutes.typeAheads.chargeTypesBillable(true))
                    .catch(() => console.log('Unable to get charge types.')),
                httpClient
                    .js(ApiRoutes.typeAheads.contractUnionWorkTypes())
                    .catch(() => console.log('Unable to get contract work types.')),
                httpClient
                    .js(ApiRoutes.typeAheads.contractScheduleTypes())
                    .catch(() => console.log('Unable to get contract work types.')),
                httpClient
                    .js(ApiRoutes.USCounties.groups())
                    .catch(() => console.log('Unable to get contract work types.')),
                httpClient
                    .js(ApiRoutes.typeAheads.billingCycles())
                    .catch(() => console.log('Unable to get billing cycles.')),
                httpClient.js(ApiRoutes.typeAheads.jobResourceTypes())
                    .catch(() => console.log('Unable to get resource types.')),
                httpClient.js(ApiRoutes.typeAheads.equipmentTypes())
                    .catch(() => console.log('Unable to get equipment types.')),
                httpClient.js(ApiRoutes.typeAheads.rentalUnitTypes())
                    .catch(() => console.log('Unable to get rental unit types.')),
                httpClient.js(ApiRoutes.userGroup.all())
                    .catch(() => console.log('Unable to get user groups.')),
                httpClient.js(ApiRoutes.certification.all())
                    .catch(() => console.log('Unable to get certifications.')),
                httpClient.js(ApiRoutes.typeAheads.employeeGroups()),
                httpClient.js(ApiRoutes.typeAheads.jobTypes()),
                httpClient.js(ApiRoutes.typeAheads.operationalCategories())
                    .catch(() => console.log('Unable to get operational categories.')),
                httpClient
                    .js(ApiRoutes.typeAheads.USStatesbyCountryId(this.context.tenant.countryId)),
                httpClient
                    .js(ApiRoutes.typeAheads.certifiedPayrollForms()),
            ]);

            const glStringsEnabled = Boolean(
                this.context.tenant?.tenantSettings?.glStringsEnabled,
            );

            const enableContractDateRange = Boolean(
                this.context.tenant?.tenantSettings?.enableContractDateRange,
            );

            const enableAutoEscalatingContractRates = Boolean(
                this.context.tenant?.tenantSettings
                    ?.enableAutoEscalatingContractRates,
            );

            const autoCalcDriveTimeBillingEnabled = Boolean(
                this.context.tenant?.tenantSettings?.autoCalculateDriveTimeBilling,
            );

            const enablePrevailingWageContracts = Boolean(
                this.context.tenant?.tenantSettings
                    ?.enablePrevailingWageContracts,
            );

            const contractsCanEnforceProjectManagers = Boolean(
                this.context.tenant?.tenantSettings?.contractsCanEnforceProjectManagers,
            );

            const showWorkCardOnTimesheet = Boolean(
                this.context.tenant?.tenantSettings?.showWorkCardOnTimesheet,
            );

            let operationsCenters = [];
            let isIntercompanyCustomer = false;
            let customForms = [];

            if (contract_id) {
                if (contract?.companyId) {
                    chargeTypeGroups = await httpClient
                        .js(ApiRoutes.typeAheads.chargeTypeGroups())
                        .catch(() => console.log('Unable to get charge type groups.'));

                    const [centers, isIntercomany, customFormsForContract] = await Promise.all([
                        httpClient
                            .js(
                                ApiRoutes.company.customerOperationsCentersCompanies(
                                    contract.companyId,
                                ),
                            )
                            .catch(() => console.log(
                                'Unable to get the operations centers.',
                            )),
                        httpClient.js(
                            ApiRoutes.company.isIntercompany(contract.companyId),
                        ),
                        httpClient.post(ApiRoutes.customForm.forContext(), {
                            companyId: contract.companyId, formContextId: CustomFormContext.Job, contractId: contract_id,
                        }),
                    ]);
                    if (centers) {
                        operationsCenters = centers.map((x) => ({ label: x.companyName, value: x.id }));
                    }
                    if (customForms?.length) {
                        customForms = customFormsForContract;
                    }
                    isIntercompanyCustomer = isIntercomany;
                }
            }

            let billingContacts = [];

            if (contract_id) {
                if (contract?.companyId) {
                    const [contacts] = await Promise.all([
                        httpClient
                            .js(
                                ApiRoutes.typeAheads.billingContacts(
                                    contract.companyId,
                                ),
                            )
                            .catch(() => console.log(
                                'Unable to get the billing contacts.',
                            )),
                    ]);
                    if (contacts) {
                        billingContacts = contacts.map((x) => ({ label: x.label, value: x.value }));
                    }
                }
            }

            if (salesOrder && salesOrder.id != null) {
                await this.newSalesOrderContract(contract, salesOrder, customers, billingCompanies);
            }

            if (!contract_id) {
                contract.effectiveDate = getInputFormat(new Date());

                const activeBillingCompanies = billingCompanies?.filter((c) => c.active === 'Active') ?? [];
                if (!contract.billingCompanyId && activeBillingCompanies.length === 1) {
                    contract.billingCompanyId = activeBillingCompanies[0].value;
                }
            }

            const originalData = JSON.stringify({ ...contract });

            this.setState({
                chargeTypes,
                contractStatuses,
                contractTemplates: [...contractTemplates],
                dispatchLocations,
                loading: false,
                contract,
                originalData,
                customers: customers.map((x) => ({ label: x.companyName, value: x.id })),
                operationsCenters,
                saving: false,
                billingCompanies,
                billingContacts,
                invoiceTerms,
                invoiceMappings,
                chargeTypeGroups,
                enableContractDateRange,
                usStates,
                enableAutoEscalatingContractRates,
                enablePrevailingWageContracts,
                glStringsEnabled,
                contractUnionWorkTypes,
                contractScheduleTypes,
                usCountyGroups,
                contractsCanEnforceProjectManagers,
                billingCycles,
                showWorkCardOnTimesheet,
                isIntercompanyCustomer,
                resourceTypes,
                equipmentTypes,
                rentalUnitTypes,
                resGroups,
                certifications,
                employeeGroups,
                jobTypes,
                operationalCategories,
                autoCalcDriveTimeBillingEnabled,
                customForms,
                cpForms,
            });
        }
    }

    contractHasProjectsEnabled = (contract) => !!(
        contract?.isProjectContract
        || this.context.tenant?.tenantSettings?.useContractProjects === 2
    );

    resetForm = () => this.setState({ formValidated: false, saving: false });

    // -- Certifications -------------------------------------------------------
    toggleCertificationModal = () => {
        const { contract } = this.state;

        Modal.show({
            title: 'Certifications',
            onSubmit: (values) => this.onAddCertifications(values),
            SaveButtonProps: {
                label: 'Add',
            },
            body: () => (
                <ValidatedSelect
                    id="selectedCertifications"
                    name="selectedCertifications"
                    label="Certification(s)"
                    isMulti
                    options={this.state.certifications
                        // omit ones already on the contract
                        .filter((c) => !(contract.contractCertifications ?? []).some((k) => k.certificationId === c.id))}
                    getOptionLabel={(o) => o.name ?? o.label}
                    getOptionValue={(o) => o.id}
                    valueField="id"
                    required
                    error="You must select a certification"
                />
            ),
        });
    };

    onAddCertifications = ({ selectedCertifications }) => {
        const { contract } = this.state;

        const already = new Set((contract.contractCertifications ?? []).map((c) => c.certificationId));

        const additions = selectedCertifications
            .filter((c) => !already.has(c.id))
            .map((c) => ({
                certificationId: c.id,
                certificationName: c.name ?? c.label,
                abbreviation: c.abbreviation,
            }));

        contract.contractCertifications = [
            ...(contract.contractCertifications ?? []),
            ...additions,
        ];

        this.setState({
            contract,
        });
    };

    onRemoveCertification = (index) => {
        const { contract } = this.state;
        contract.contractCertifications.splice(index, 1);
        this.setState({ contract });
    };


    update = async (contract) => {
        const response = await httpClient.andGetResponse(
            httpClient.types.put,
            ApiRoutes.contract.update(contract.id),
            { ...contract },
            'Error Saving Contract',
        );

        if (response.result === ServiceResponseResult.Ok) {
            toasty.success('Contract saved.');

            this.context.history.go(0);
        } else {
            toasty.error('Problem saving the contract:', response.message, {
                autoClose: false,
                closeOnClick: true,
            });
        }
        this.setState({ saving: false });
    };

    updateChargeTypeEligibility = (chargeTypeId, eligibilityId) => {
        const { contract } = { ...this.state };

        contract.contractChargeTypes = contract.contractChargeTypes.map((chargeType) => {
            if (chargeType.chargeTypeId === chargeTypeId) {
                return {
                    ...chargeType,
                    chargeTypeEligibilityId: eligibilityId,
                };
            }

            return chargeType;
        });

        this.setState({ contract: { ...contract } });
    };

    onContractStateChanged = (value) => {
        const { contract } = { ...this.state };

        contract.usStates = value;

        this.setState({ contract });
    };

    onContractJobTypeChanged = (value) => {
        const { contract } = { ...this.state };
        contract.contractJobTypes = value.map((v) => ({ id: uuid(), jobTypeId: v.value }));

        this.setState({ contract });
    };

    renderContractChargeTypeGroupsRows = () => {
        const { contract } = { ...this.state };
        const { contractChargeTypeGroups } = contract;

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const currencyCode = tenantSettings.defaultCurrencyCode || 'USD';

        if (!contractChargeTypeGroups) return '';

        const visibleChargeTypeGroups = contractChargeTypeGroups.filter(
            (x) => x.chargeTypeGroupStatusId !== _ChargeTypeGroupStatuses.Inactive,
        );

        return visibleChargeTypeGroups.map((chargeTypeGroup) => {
            let overiddenContractChargeType = '';
            if (chargeTypeGroup.billingRates != null && chargeTypeGroup.billingRates.length > 0) {
                const [{ billingRate }] = chargeTypeGroup.billingRates;

                overiddenContractChargeType = `Rate ${billingRate.rate != null ? formatCurrency(billingRate.rate, currencyCode) : ' '}`;
            }

            const hasOverride = chargeTypeGroup.billingRates?.length > 0 || chargeTypeGroup.usCountyGroupBillingRates?.length > 0;

            if (typeof chargeTypeGroup.chargeTypeGroupBillingRates === 'undefined') {
                chargeTypeGroup.chargeTypeGroupBillingRates = '';
            }

            const redirectToOrgUrl = `./organization/${contract.companyId}#billingRates`;

            const selectedWorkType = this.getContractUnionWorkType();

            return (
                <tr key={chargeTypeGroup.chargeTypeGroupId}>
                    <td>
                        {chargeTypeGroup.invoiceDescription}
                    </td>
                    <td>{chargeTypeGroup?.chargeTypes ?? ''}</td>
                    {selectedWorkType?.isUnionWork ? (
                        <td>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {hasOverride ? (
                                    <>
                                        <Button
                                            id={`edit-ctg-${chargeTypeGroup.id}`}
                                            onClick={() => this.onEditGroupRate(chargeTypeGroup)}
                                            color="link"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>

                                        <UncontrolledTooltip
                                            target={`edit-ctg-${chargeTypeGroup.id}`}
                                        >
                                            Edit Union Rates
                                        </UncontrolledTooltip>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            id={`add-ctg-${chargeTypeGroup.id}`}
                                            onClick={() => this.onAddGroupRate(chargeTypeGroup)}
                                            color="link"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </Button>

                                        <UncontrolledTooltip
                                            target={`add-ctg-${chargeTypeGroup.id}`}
                                        >
                                            Add Union Rates
                                        </UncontrolledTooltip>
                                    </>
                                )}
                            </div>
                        </td>
                    ) : (
                        <>
                            <td>
                                <table
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                    }}
                                >
                                    <tbody>
                                        <tr>
                                            <td
                                                style={{
                                                    border: 'none',
                                                    padding: 0,
                                                }}
                                            >
                                                <pre className="mb-0">
                                                    {chargeTypeGroup.chargeTypeGroupBillingRates}
                                                </pre>
                                            </td>

                                            {chargeTypeGroup.chargeTypeGroupBillingRates.length > 0 && (
                                                <td
                                                    style={{
                                                        cursor: 'pointer',
                                                        border: 'none',
                                                        padding: 0,
                                                        paddingLeft: '6px',
                                                        width: '100%',
                                                        height: '100%',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    <Link
                                                        to={redirectToOrgUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <FontAwesomeIcon icon={faInfoCircle} />
                                                    </Link>
                                                </td>
                                            )}
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                            <td style={{ verticalAlign: 'middle' }}>
                                <table
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                    }}
                                >
                                    <tbody>
                                        <tr>
                                            <td
                                                style={{
                                                    border: 'none',
                                                    padding: 0,
                                                }}
                                            >
                                                <pre>
                                                    {overiddenContractChargeType}
                                                </pre>
                                            </td>

                                            {overiddenContractChargeType.length > 0 ? (
                                                <td
                                                    style={{
                                                        border: 'none',
                                                        padding: 0,
                                                        paddingLeft: '6px',
                                                        width: '100%',
                                                        height: '100%',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    <Button
                                                        color="link"
                                                        onClick={() => this.onEditGroupRate(chargeTypeGroup)}
                                                        className="p-0"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faEdit}
                                                            size="md"
                                                        />
                                                    </Button>
                                                </td>
                                            ) : (
                                                <td
                                                    style={{
                                                        border: 'none',
                                                        padding: 0,
                                                        paddingLeft: '6px',
                                                        width: '100%',
                                                        height: '100%',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <Button
                                                        color="link"
                                                        onClick={() => this.onAddGroupRate(chargeTypeGroup)}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faPlusCircle}
                                                            size="md"
                                                        />
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </>
                    )}
                    <Can
                        I="edit"
                        a="contract"
                    >
                        <td className="text-center">
                            <FontAwesomeIcon
                                icon={faTimes}
                                title={`Remove ${chargeTypeGroup.description}`}
                                className="text-danger cursor-pointer"
                                onClick={() => this.onRemoveCTGroup(
                                    chargeTypeGroup.chargeTypeGroupId,
                                )}
                            />
                        </td>
                    </Can>
                </tr>
            );
        });
    };

    renderContractChargeTypesRows = () => {
        const { contract } = { ...this.state };
        const { contractChargeTypes } = contract;

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};

        const isTenantBillingRatesAllowed = !!tenantSettings.billingRatesEnabled;
        const currencyCode = tenantSettings.defaultCurrencyCode || 'USD';

        if (!contractChargeTypes) return '';

        return contractChargeTypes.map((chargeType) => {
            // User must select an eligibility
            const eligibilityValid = !!this.state.formValidated
                && !chargeType.chargeTypeEligibilityId;

            // Is this an exception for the currently selected customer on the contract?
            const isException = (
                (
                    (chargeType.configurations ?? []).find(
                        (x) => x.chargeTypeConfigurationTypeId
                            == ChargeTypeConfigurationType.Exception,
                    ) ?? {}
                ).customers ?? []
            ).includes(contract.companyId);

            let overiddenContractChargeType = '';
            const hasOverride = chargeType.billingRates?.length > 0 || chargeType.usCountyGroupBillingRates?.length > 0;

            if (chargeType.billingRates != null && chargeType.billingRates.length > 0) {
                const [{ billingRate }] = chargeType.billingRates;
                overiddenContractChargeType = `Rate ${billingRate.rate != null ? formatCurrency(billingRate.rate, currencyCode) : ' '
                    }\r\n`
                    + `OT ${billingRate.otRate != null ? formatCurrency(billingRate.otRate, currencyCode) : ' '
                    }`;

                if (tenantSettings.enableDoubleTimeBilling) {
                    overiddenContractChargeType += `\r\nDT ${billingRate.altRate != null ? formatCurrency(billingRate.altRate, currencyCode) : ' '}`;
                }
                if (tenantSettings.enablePremiumBilling) {
                    overiddenContractChargeType += `\r\nPR ${billingRate.premiumRate != null ? formatCurrency(billingRate.premiumRate, currencyCode) : ' '}`;
                    overiddenContractChargeType += `\r\nPR OT ${billingRate.otPremiumRate != null ? formatCurrency(billingRate.otPremiumRate, currencyCode) : ' '}`;
                }
            }


            if (chargeType.chargeTypeBillingRates == undefined) chargeType.chargeTypeBillingRates = '';

            const redirectToOrgUrl = `/organization/${contract.companyId}#billingRates`;

            const selectedWorkType = this.getContractUnionWorkType();
            return (
                <tr key={chargeType.chargeTypeId}>
                    <td className="text-center">{chargeType.order ?? ''}</td>
                    <td>
                        {chargeType.description}
                        {!!isException && (
                            <Badge
                                color="warning"
                                className="exception-badge ml-2"
                            >
                                E
                            </Badge>
                        )}
                    </td>
                    <td className="text-center">{chargeType.chargeUnits}</td>
                    <td>{chargeType.group}</td>
                    <td>{chargeType.employees}</td>
                    <td>{chargeType.equipment}</td>

                    {selectedWorkType?.isUnionWork ? (
                        <td>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {hasOverride ? (
                                    <>
                                        <Button
                                            id={`edit-ct-${chargeType.id}`}
                                            onClick={() => this.onEditRate(chargeType)}
                                            color="link"
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>

                                        <UncontrolledTooltip
                                            target={`edit-ct-${chargeType.id}`}
                                        >
                                            Edit Union Rates
                                        </UncontrolledTooltip>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            id={`add-ct-${chargeType.id}`}
                                            onClick={() => this.onAddRate(chargeType)}
                                            color="link"
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                        </Button>

                                        <UncontrolledTooltip
                                            target={`add-ct-${chargeType.id}`}
                                        >
                                            Add Union Rates
                                        </UncontrolledTooltip>
                                    </>
                                )}
                            </div>
                        </td>
                    ) : (
                        <>
                            {// Column count must match chargeTypeHeaderLabels in render method.
                                isTenantBillingRatesAllowed && (
                                    <>
                                        <td>
                                            <table
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                }}
                                            >
                                                <tbody>
                                                    <tr>
                                                        <td
                                                            style={{
                                                                border: 'none',
                                                                padding: 0,
                                                            }}
                                                        >
                                                            <pre className="mb-0">
                                                                {chargeType.chargeTypeBillingRates}
                                                            </pre>
                                                        </td>
                                                        {chargeType.chargeTypeBillingRates.length > 0 && (
                                                            <td
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    border: 'none',
                                                                    padding: 0,
                                                                    paddingLeft: '6px',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                <Link
                                                                    to={
                                                                        redirectToOrgUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    <FontAwesomeIcon icon={faInfoCircle} />
                                                                </Link>
                                                            </td>
                                                        )}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <table
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                }}
                                            >
                                                <tbody>
                                                    <tr>
                                                        <td
                                                            style={{
                                                                border: 'none',
                                                                padding: 0,
                                                            }}
                                                        >
                                                            <pre>
                                                                {overiddenContractChargeType}
                                                            </pre>
                                                        </td>

                                                        {overiddenContractChargeType.length > 0 ? (
                                                            <td
                                                                style={{
                                                                    border: 'none',
                                                                    padding: 0,
                                                                    paddingLeft: '6px',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                <Button
                                                                    color="link"
                                                                    onClick={() => this.onEditRate(chargeType)}
                                                                    className="p-0"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faEdit}
                                                                        size="md"
                                                                    />
                                                                </Button>
                                                            </td>
                                                        ) : (
                                                            <td
                                                                style={{
                                                                    border: 'none',
                                                                    padding: 0,
                                                                    paddingLeft: '6px',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                <Button
                                                                    color="link"
                                                                    onClick={() => this.onAddRate(chargeType)}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faPlusCircle}
                                                                        size="md"
                                                                    />
                                                                </Button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </td>
                                    </>
                                )}
                        </>
                    )}
                    <td className="text-center">
                        <YesNoBadge value={chargeType.billable} />
                    </td>
                    <Can
                        I="edit"
                        a="contract"
                    >
                        <CommonContext.Consumer>
                            {(value) => (
                                <>
                                    <td
                                        className={cls({
                                            'p-0': eligibilityValid,
                                        })}
                                    >
                                        <div
                                            className={cls('h-100 w-100', {
                                                'p-2 border border-danger':
                                                    eligibilityValid,
                                            })}
                                        >
                                            <ButtonGroup
                                                disabled={
                                                    !!value.formIsOpen
                                                }
                                            >
                                                {ChargeTypeEligibilities.map(
                                                    (
                                                        eligibility,
                                                        eligibilityIndex,
                                                    ) => (
                                                        <Button
                                                            type="button"
                                                            color="secondary"
                                                            size="sm"
                                                            key={
                                                                eligibilityIndex
                                                            }
                                                            className={cls(
                                                                'pt-0 pb-0 pl-1 pr-1 mr-1',
                                                                {
                                                                    active:
                                                                        eligibility.id
                                                                        === chargeType.chargeTypeEligibilityId,
                                                                },
                                                            )}
                                                            onClick={() => {
                                                                this.updateChargeTypeEligibility(
                                                                    chargeType.chargeTypeId,
                                                                    eligibility.id,
                                                                );
                                                            }}
                                                        >
                                                            {
                                                                eligibility.name
                                                            }
                                                        </Button>
                                                    ),
                                                )}
                                            </ButtonGroup>
                                            <Input
                                                className="hidden-validation"
                                                required
                                                value={chargeType.chargeTypeEligibilityId ?? ''}
                                                error="Eligibility selection is required."
                                            />
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            title={`Remove ${chargeType.description}`}
                                            className="text-danger cursor-pointer"
                                            onClick={() => this.onRemoveChargeType(
                                                chargeType.chargeTypeId,
                                            )}
                                        />
                                    </td>
                                </>
                            )}
                        </CommonContext.Consumer>
                    </Can>
                </tr>
            );
        });
    };

    getUSCountyGroups = (chargeType, edit = false, existingRates = []) => {
        const { contract, usCountyGroups } = this.state;
        const { usStates, contractUnionWorkTypeId } = contract;

        const isUnionWorkEnforced = !!(this.context.tenant.tenantSettings.enforceContractUnionWork);

        if (!usStates?.length || !usCountyGroups?.length || !isUnionWorkEnforced) {
            return [];
        }

        const groups = usCountyGroups.filter((group) => usStates.find((state) => state.id === group.usStateId)
            && group.contractUnionWorkTypes.includes(contractUnionWorkTypeId) // Only include zones that support the selected work type
            && group.usCountyGroupTypeId === 1, // Only include zones, no locals
        );

        return groups.reduce((results, group, index) => {
            const existingRate = existingRates?.find((rate) => rate.usCountyGroupId === group.id);

            if (existingRate) {
                const selectedRate = { ...new ContractChargeTypeUSCountyGroupBillingRate() };

                const cloned = cloneUtil(existingRate);
                selectedRate.id = cloned.id;
                selectedRate.billingRate = cloned.billingRate;
                selectedRate.billingRateId = cloned.billingRateId;
                selectedRate.chargeTypeId = chargeType.chargeTypeId;
                selectedRate.chargeTypeName = chargeType.description;
                selectedRate.contractId = chargeType.contractId;
                selectedRate.usCountyGroupId = group.id;
                selectedRate.usCountyGroup = group;
                selectedRate.billingRate.current = true;

                return {
                    ...results,
                    [index]: selectedRate,
                };
            } else {
                const rate = new ContractChargeTypeUSCountyGroupBillingRate();
                rate.chargeTypeId = chargeType.chargeTypeId;
                rate.chargeTypeName = chargeType.description;
                rate.contractId = chargeType.contractId;
                rate.usCountyGroupId = group.id;
                rate.usCountyGroup = group;
                rate.billingRate.current = !edit;

                return {
                    ...results,
                    [index]: rate,
                };
            }
        }, {});
    };

    getJobTypeRates = (chargeType, edit = false, existingRates = []) => {
        const { contract, jobTypes, usCountyGroups } = this.state;
        const { contractJobTypes } = contract;

        const selectedWorkType = this.getContractUnionWorkType();

        const isBillingPerJobTypeAllowed = !!(this.context.tenant.tenantSettings.enableContractJobTypeBillingSetting);

        if (!contractJobTypes?.length || !isBillingPerJobTypeAllowed || (selectedWorkType?.isUnionWork == true && usCountyGroups?.length)) {
            return [];
        }

        return contractJobTypes.map((cjt) => {
            const existingRate = existingRates?.find((rate) => rate.jobTypeId === cjt.jobTypeId);
            const jobType = jobTypes.find((j) => j.value == cjt.jobTypeId);

            if (existingRate) {
                const selectedRate = { ...new ContractChargeTypeJobTypeBillingRate() };

                const cloned = cloneUtil(existingRate);
                selectedRate.id = cloned.id;
                selectedRate.billingRate = cloned.billingRate;
                selectedRate.billingRateId = cloned.billingRateId;
                selectedRate.chargeTypeId = chargeType.chargeTypeId;
                selectedRate.chargeTypeName = chargeType.description;
                selectedRate.contractId = chargeType.contractId;
                selectedRate.jobTypeId = cjt.jobTypeId;
                selectedRate.jobTypeName = jobType.label;
                selectedRate.billingRate.current = true;

                return selectedRate;
            } else {
                const rate = new ContractChargeTypeJobTypeBillingRate();
                rate.chargeTypeId = chargeType.chargeTypeId;
                rate.chargeTypeName = chargeType.description;
                rate.contractId = chargeType.contractId;
                rate.jobTypeId = cjt.jobTypeId;
                rate.jobTypeName = jobType.label;
                rate.billingRate.current = !edit;

                return rate;
            }
        }, {});
    };

    getContractUnionWorkType = () => {
        const { contract, contractUnionWorkTypes } = this.state;

        return (
            contractUnionWorkTypes ?? []
        ).find(
            (x) => contract.contractUnionWorkTypeId
                === x.value,
        ) ?? '';
    };

    renderContractPaySchedules = () => {
        const { contract } = { ...this.state };
        const { contractPaySchedules } = contract;

        if (!contractPaySchedules) return '';

        return contractPaySchedules.map(
            (contractPaySchedule, contractPayScheduleIndex) => (
                <tr key={contractPayScheduleIndex}>
                    <td>{contractPaySchedule.paySchedule.governingBody}</td>
                    <td>{contractPaySchedule.paySchedule.state}</td>
                    <td>
                        {contractPaySchedule.paySchedule.counties == null
                            || contractPaySchedule.paySchedule.counties.length == 0
                            ? 'All'
                            : contractPaySchedule.paySchedule.counties}
                    </td>
                    <td>{contractPaySchedule.paySchedule.reg}</td>
                    <td>{contractPaySchedule.paySchedule.ot}</td>
                    <td>{contractPaySchedule.paySchedule.fringe}</td>
                    <td className="text-center">
                        <FontAwesomeIcon
                            icon={faTimes}
                            title={`Remove ${contractPaySchedule.governingBody}`}
                            className="text-danger cursor-pointer"
                            onClick={() => this.onRemovePaySchedule(
                                contractPayScheduleIndex,
                            )}
                        />
                    </td>
                </tr>
            ),
        );
    };

    renderUSContractCountyGroups = () => {
        const { contract } = { ...this.state };
        const { contractUSCountyGroups } = contract;

        if (!contractUSCountyGroups) return '';

        return contractUSCountyGroups.map(
            (contractCountyGroup, contractCountyGroupIndex) => (
                <tr key={contractCountyGroupIndex}>
                    <td>{contractCountyGroup.usCountyGroup?.description}</td>
                    <td>{contractCountyGroup.usCountyGroup?.state}</td>
                    <td>
                        {contractCountyGroup.usCountyGroup?.counties == null
                            || contractCountyGroup.usCountyGroup.counties.length == 0
                            ? 'All'
                            : contractCountyGroup.usCountyGroup.counties}
                    </td>
                    <td className="text-center">
                        <FontAwesomeIcon
                            icon={faTimes}
                            title={`Remove ${contractCountyGroup.description}`}
                            className="text-danger cursor-pointer"
                            onClick={() => this.onRemoveCountyGroup(
                                contractCountyGroupIndex,
                            )}
                        />
                    </td>
                </tr>
            ),
        );
    };

    onRemoveCountyGroup = (contractCountyGroupIndex) => {
        const { contract } = { ...this.state };

        const countyGroups = [...contract.contractUSCountyGroups];
        countyGroups.splice(contractCountyGroupIndex, 1);
        contract.contractUSCountyGroups = [...countyGroups];

        this.setState({ contract: { ...contract } });
    };

    onAddCountyGroups = () => {
        const { contract } = { ...this.state };
        const { contractUSCountyGroups } = contract;

        const itemsToOmit = (contractUSCountyGroups ?? []).map((c) => c.usCountyGroupId) ?? [];

        this.uSCountyGroupSelectorRef.current.open(null, [...itemsToOmit]);
    };

    onAddUSCountyGroupCallback = (selections) => {
        const { contract } = { ...this.state };
        let updated = [...contract.contractUSCountyGroups] ?? [];

        // get selections from the popup
        const selectedUSCountyGroups = [...selections].map((x) => ({
            id: 0,
            contractId: contract.id,
            usCountyGroupId: x.id,
            usCountyGroup: {
                id: x.id,
                description: x.description,
                state: x.state,
                counties: x.counties,
            },
        }));


        // merge additions with current charges
        updated = updated.concat([...selectedUSCountyGroups]);
        contract.contractUSCountyGroups = [...updated];

        this.setState({ contract: { ...contract } });
    };
    // #region JOBS
    renderJobRows = (jobs = []) => {
        const { number } = this.state.contract;
        if (!jobs) return '';
        return jobs.map((job) => (
            <tr key={job.id}>
                <td className="ct-job-number">
                    <Link
                        className="site-link"
                        title="View this Job"
                        to={`${AppNavPaths.Job}/${job.id}`}
                    >
                        {`${number}-${job.contractSequence}`}
                    </Link>
                </td>
                <td className="ct-job-name">{job.description}</td>
                <td className="ct-job-week">{job.weekOfDate}</td>
                <td className="ct-job-active">
                    <YesNoBadge value />
                </td>
            </tr>
        ));
    };

    renderContractJobsTable = ({ entities, showAddButton = false }) => {
        const { contract } = this.state;

        return (
            <SimpleTable
                id="contractJobTable"
                scrollable
                maxBodyHeight="500px"
                noTopBorder
                permission="contract"
                tableHeaderLabels={[
                    {
                        name: 'Job #',
                        class: 'ct-job-number',
                    },
                    {
                        name: 'Job Name',
                        class: 'ct-job-name',
                    },
                    {
                        name: 'Week',
                        class: 'ct-job-week',
                    },
                    {
                        name: 'Active?',
                        class: 'ct-job-active',
                    },
                ]}
                editable={false}
                entities={entities}
                rowRenderer={() => this.renderJobRows(entities)}
                noDataText="No jobs found."
                leftToolbarContent={(
                    <span className="pl-1 text-muted font-weight-bold">
                        Most recent 2 contract weeks
                        <Link
                            className="ml-2 site-link"
                            to={`${AppNavPaths.Jobs}?contractNumber=${contract.number}`}
                        >
                            (View All)
                        </Link>
                    </span>
                )}
                {...(showAddButton ? {
                    addLabelText: 'Add Job',
                    addButtonEnabled: contract?.contractStatusId === _ContractStatuses.Active,
                    onAddFunction: this.onAddJob,
                } : {})}
            />
        );
    };

    renderProjectRows = () => {
        const { projects } = this.state.contract;
        const { number } = this.state.contract;
        if (!projects) return '';
        return projects.map((project) => (
            <tr key={project.id}>
                <td className="ct-job-number">
                    <Link
                        className="site-link"
                        title="View this Project"
                        to={`${AppNavPaths.Project}/${project.id}`}
                    >
                        {`${number}-${project.contractSequence}`}
                    </Link>
                </td>
                <td className="ct-job-active">
                    <YesNoBadge value />
                </td>
            </tr>
        ));
    };

    renderContractProjectsTable = () => {
        const { contract } = this.state;

        return (
            <SimpleTable
                id="contractProjectTable"
                scrollable
                maxBodyHeight="500px"
                noTopBorder
                permission="contract"
                addLabelText="Add Project"
                addButtonEnabled={
                    contract?.contractStatusId === _ContractStatuses.Active
                }
                onAddFunction={this.onAddProject}
                tableHeaderLabels={[
                    {
                        name: 'Project #',
                        class: 'ct-project-number',
                    },
                    {
                        name: 'Active?',
                        class: 'ct-project-active',
                    },
                ]}
                editable={false}
                entities={contract.projects}
                rowRenderer={this.renderProjectRows}
                noDataText="No projects found."
                leftToolbarContent={(
                    <span className="pl-1 text-muted font-weight-bold">
                        Projects
                        <Link
                            className="ml-2 site-link"
                            to={`${AppNavPaths.Projects}?n=${contract.number}&i=${contract.id}`}
                        >
                            (View All)
                        </Link>
                    </span>
                )}
            />
        );
    };

    renderJobAndProjectSections = ({
        contractJobs,
        hasContractJobs,
        projectsEnabledForContract,
        hasJobRelatedCustomForms = false,
    }) => {
        if (hasJobRelatedCustomForms) {
            return (
                <>
                    <SubHeading>Jobs</SubHeading>
                    {this.renderContractJobsTable({
                        entities: contractJobs,
                        showAddButton: true,
                    })}
                </>
            );
        }

        if (projectsEnabledForContract) {
            return (
                <>
                    {hasContractJobs && (
                        <>
                            <SubHeading>Jobs</SubHeading>
                            {this.renderContractJobsTable({
                                entities: contractJobs,
                            })}
                        </>
                    )}

                    <SubHeading>Projects</SubHeading>
                    {this.renderContractProjectsTable()}

                    {Boolean(this.state.contract?.createdFromId) && (
                        <ContractProjectProgressBar
                            label="Contract Progress"
                            className="mt-4"
                            contractId={this.state.contract.id}
                            canViewDetails={this.context.checkUserHasPermission(
                                ApplicationPermissions.contract_view,
                            )}
                        />
                    )}
                </>
            );
        }

        return (
            <>
                <SubHeading>Jobs</SubHeading>
                {this.renderContractJobsTable({
                    entities: contractJobs,
                    showAddButton: true,
                })}
            </>
        );
    };

    onChangeContractNumber = async ({ newContractNumber }) => {
        const { contract } = this.state;

        if (!newContractNumber || newContractNumber.trim() === '') {
            toasty.error('Number is required.');
            return;
        }

        const oldContractNumber = contract.number;
        if (oldContractNumber === newContractNumber.trim()) {
            toasty.error('The new contract number must be different from the current contract number.');
            return;
        }

        try {
            const updatedContract = { ...contract, number: newContractNumber.trim() };
            const response = await httpClient.andGetResponse(
                httpClient.types.put,
                ApiRoutes.contract.changeContractNumber(),
                updatedContract,
                'Error Changing Contract Number',
            );
            if (response.result === ServiceResponseResult.Ok) {
                toasty.success('Contract number updated.');
                this.setState({ contract: updatedContract, originalData: updatedContract });
                this.context.history.push(`${AppNavPaths.Contract}/${response.data}`);
            } else {
                contract.number = oldContractNumber;
                toasty.error('Error Changing Contract Number:', response.message, {
                    autoClose: false,
                    closeOnClick: true,
                });
            }
        } catch (e) {
            console.error(e);
            toasty.error(
                'Error Changing Contract Number.',
            );
        }
    };

    onContractTemplateChanged = async ({ value }) => {
        this.setState({ contractTemplateLoading: true });

        const contractTemplate = await httpClient.js(ApiRoutes.contractTemplate.byId(value));
        const contract = cloneUtil(this.state.contract);

        delete contractTemplate.id;
        delete contractTemplate.description;

        this.setState({
            contractTemplateLoading: false,
            contract: {
                ...contract,
                ...contractTemplate,
                contractTemplateId: value,
                contractJobTypes: (contractTemplate.contractTemplateJobTypes ?? []).map((j) => ({
                    id: uuid(),
                    jobTypeId: j.jobTypeId,
                })),
                bumpRules: (contractTemplate.contractTemplateBumpRules ?? []).map((j) => ({
                    id: uuid(),
                    bumpHours: j.bumpHours,
                    appliesToCancellation: j.appliesToCancellation,
                })),
            },
        });
    };

    renderEndAddon = () => {
        const { contract } = this.state;
        const { id } = this.props.match.params;
        if (id && contract.number &&
            !contract.createdFromId &&
            contract.contractStatusId === _ContractStatuses.Active) {
            return (
                <>
                    <IconButton
                        id="changeContractTooltip"
                        onClick={() => this.showChangeContractNumberModal()}
                    >
                        <FontAwesomeIcon
                            icon={faSync}
                            className="text-muted"
                        />
                    </IconButton>
                    <UncontrolledTooltip
                        placement="top"
                        target="changeContractTooltip"
                    >
                        Change Contract Number
                    </UncontrolledTooltip>

                </>
            );
        }
        return null;
    };

    showChangeContractNumberModal = () => {
        Modal.show({
            title: 'Change Contract Number',
            onSubmit: (values) => this.onChangeContractNumber(values),
            body: () => (
                <>
                    <TipAlert
                        tipText="Note"
                        label="The existing contract will be deactivated, and a new contract with the same details will be created using the new contract number."
                    />

                    <Input
                        label="Contract Number"
                        id="newContractNumber"
                        name="newContractNumber"
                        placeholder="Enter Number (letter and digits only)"
                        maxLength="150"
                        pattern="[a-zA-Z0-9\-]+"
                        required
                        error="A new number is required."
                    />
                </>
            ),
        });
    };

    render() {
        const {
            contract,
            contractStatuses,
            contractTemplates,
            customers,
            errors,
            formValidated,
            dispatchLocations,
            operationsCenters,
            originalData,
            saving,
            currentEmailAddress,
            billingCompanies,
            billingContacts,
            invoiceTerms,
            invoiceMappings,
            enableContractDateRange,
            enableAutoEscalatingContractRates,
            enablePrevailingWageContracts,
            autoCalcDriveTimeBillingEnabled,
            chargeTypes,
            chargeTypeGroups,
            showRateFormDelete,
            glStringsEnabled,
            contractUnionWorkTypes,
            contractScheduleTypes,
            usStates,
            contractsCanEnforceProjectManagers,
            billingCycles,
            showWorkCardOnTimesheet,
            isIntercompanyCustomer,
            resourceTypes,
            equipmentTypes,
            rentalUnitTypes,
            resGroups,
            jobTypes,
            customForms,
            cpForms,
        } = this.state;
        const existing = !!contract.id;

        // Computed
        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const combineCharges = Boolean(tenantSettings.combineRentalNonRentalChargesOnRentalInvoices);
        const customerHasOperationsCenters = (operationsCenters ?? []).length > 0;
        const hasRentalChargeTypes = (contract.contractChargeTypes ?? []).some(
            (ct) => ct.resourceTypeId === ChargeTypeResourceTypes.Rental,
        ) || (contract.contractChargeTypeGroups ?? []).some((g) => g.hasRentalChargeTypes);
        const isRentalToggleLocked = contract.hasJobsOrTimesheets || (hasRentalChargeTypes && contract.isProjectContract);
        const allowRentalChargeTypes = !!tenantSettings.allowRentalChargeTypes;

        const isLoading = !!this.state.loading
            || !this.context.tenant.tenantSettings
            || !this.context?.user;

        const selfDispatchTimesheetsEnabled = !!this.context.tenant.tenantSettings
            ?.enableSelfDispatchTimesheets;

        const doubleTimeEnabled = !!this.context.tenant.tenantSettings.enableDoubleTimePayroll;

        const timesheetCheckinsEnabled = !!this.context.tenant.tenantSettings
            ?.enableContractTimesheetCheckins;

        const showSelfDispatchOptions = !!contract?.allowSelfDispatch // Contract must be set up for self-dispatching
            && !!selfDispatchTimesheetsEnabled; // Tenant setting must also be enabled for self-dispatch timesheets.

        const billingContactIsEditable = contract?.billingContactId != null;

        const jContract = JSON.stringify({ ...contract });

        const isInvoicingAllowed = !!tenantSettings.invoicingEnabled;
        const isChargeTypeGroupsAllowed = !!tenantSettings.chargeTypeGroupsEnabled && !contract.enableNonFlaggingTimesheets;
        const isCertificationsAllowed = !!tenantSettings.enableCertification && contract?.certificationRequired;
        const isBillingRatesEnabled = !!tenantSettings.billingRatesEnabled;
        const isUnionWorkEnforced = !!tenantSettings.enforceContractUnionWork;
        const workCardOptions = getWorkCardOptions(tenantSettings);
        const isBillingPerJobTypeAllowed = !!tenantSettings.enableContractJobTypeBillingSetting;
        const isQuickRentalTemplate = !!tenantSettings.quickRentalContractTemplateId
            && contract.contractTemplateId === tenantSettings.quickRentalContractTemplateId;
        const isNumberReadOnly = !!contract.number && !!this.props.match.params.id;
        const contractJobs = (contract?.jobs ?? []).filter(
            (job) => job.isDeleted === false && !(job.projectId ?? job.ProjectId),
        );
        const hasContractJobs = contractJobs.length > 0;
        const projectsEnabledForContract = this.contractHasProjectsEnabled(contract);

        const selectedWorkType = this.getContractUnionWorkType();

        // Rates column will be available depending on whether or not the tenant
        // has billing rates enabled.
        // If you alter the columns here, update this.renderContractChargeTypesRows as well.
        let chargeTypeHeaderLabels = [];
        let chargeTypeGroupHeaderLabels = [];

        if (!isBillingRatesEnabled) {
            chargeTypeHeaderLabels = [
                { name: 'Order', class: 'text-center' },
                { name: 'Description' },
                { name: 'Charge Units', class: 'text-center' },
                { name: 'Group' },
                { name: 'Employees' },
                { name: 'Equipment' },
                { name: 'Billable', class: 'text-center' },
                { name: 'Job Eligibility' },
                { name: '', class: 'text-center' },
            ];
            chargeTypeGroupHeaderLabels = [
                { name: 'Description' },
                { name: 'Charge Type(s)' },
                { name: '', class: 'text-center' },
            ];
        } else if (selectedWorkType?.isUnionWork) {
            chargeTypeHeaderLabels = [
                { name: 'Order', class: 'text-center' },
                { name: 'Description' },
                { name: 'Charge Units', class: 'text-center' },
                { name: 'Group' },
                { name: 'Employees' },
                { name: 'Equipment' },
                { name: 'Union Rates' },
                { name: 'Billable', class: 'text-center' },
                { name: 'Job Eligibility' },
                { name: '', class: 'text-center' },
            ];
            chargeTypeGroupHeaderLabels = [
                { name: 'Description' },
                { name: 'Charge Type(s)' },
                { name: 'Union Rates' },
                { name: '', class: 'text-center' },
            ];
        } else {
            chargeTypeHeaderLabels = [
                { name: 'Order', class: 'text-center' },
                { name: 'Description' },
                { name: 'Charge Units', class: 'text-center' },
                { name: 'Group' },
                { name: 'Employees' },
                { name: 'Equipment' },
                { name: 'Org Rates' },
                { name: 'Contract Rates' },
                { name: 'Billable', class: 'text-center' },
                { name: 'Job Eligibility' },
                { name: '', class: 'text-center' },
            ];
            chargeTypeGroupHeaderLabels = [
                { name: 'Description' },
                { name: 'Charge Type(s)' },
                { name: 'Org Rates' },
                { name: 'Contract Rates' },
                { name: '', class: 'text-center' },
            ];
        }

        const operationalCategories = [
            { label: 'None', value: null },
            ...this.state.operationalCategories || [],
        ];

        const hasJobRelatedCustomForms = (customForms ?? [])?.length || contract.requiresJobApproval;

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, jContract)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="contractForm"
                    formHeadingIcon={faFileContract}
                    formHeading={!existing ? 'New Contract' : 'Edit Contract'}
                    formName="contractForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    actions={this.getActions()}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={isLoading}
                    loadingMessage="Loading Contract, please wait..."
                    backUrl={AppNavPaths.Contracts}
                    backLabel="Back to Contracts"
                >
                    <ValidatedSelect
                        id="customers"
                        name="contract.companyId"
                        label="Customer"
                        options={customers}
                        required
                        value={
                            (customers ?? []).find(
                                (s) => s.value === contract.companyId,
                            ) ?? ''
                        }
                        onChange={this.onCustomerChanged}
                        error="A customer selection is required."
                    />

                    <ValidatedSelect
                        id="operationsCenters"
                        name="contract.operationsCenterCompanyId"
                        label="Operations Center"
                        options={operationsCenters}
                        required={
                            !!customerHasOperationsCenters
                            && contract.operationsCentersApply
                        }
                        value={
                            (operationsCenters ?? []).find(
                                (s) => s.value
                                    === contract.operationsCenterCompanyId,
                            ) ?? ''
                        }
                        onChange={this.onSelectChanged}
                        error="Operations Center is required."
                        formGroupHidden={
                            !customerHasOperationsCenters
                            || !contract.operationsCentersApply
                        }
                    />

                    <Input
                        label="Contract Number"
                        id="number"
                        name="contract.number"
                        value={contract.number ?? ''}
                        onChange={this.onChange}
                        readOnly={isNumberReadOnly}
                        placeholder="Enter Number (letter and digits only)"
                        maxLength="150"
                        pattern="[a-zA-Z0-9\-]+"
                        required
                        error="Number is required."
                        startAddon={isQuickRentalTemplate && !isNumberReadOnly ? (
                            <span className="text-muted">QR-</span>
                        ) : undefined}
                        endAddon={this.renderEndAddon()}
                    />
                    {isQuickRentalTemplate && !isNumberReadOnly && (
                        <TipAlert
                            tipText="Note"
                            label="Quick rental contract numbers must begin with QR-. The prefix will be added automatically on save."
                        />
                    )}
                    <Input
                        label="Description"
                        id="description"
                        name="contract.description"
                        value={contract.description ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter Description (max 500 characters)"
                        maxLength="500"
                        required
                        error="Please enter a description"
                    />

                    <ValidatedSelect
                        id="dispatchLocations"
                        name="contract.dispatchLocations"
                        label="Dispatch Locations"
                        options={dispatchLocations}
                        isMulti
                        required
                        value={
                            (dispatchLocations ?? []).filter((x) => (
                                contract.dispatchLocations ?? []
                            ).includes(x.value)) ?? ''
                        }
                        onChange={this.onSelectChanged}
                        error="A Dispatching selection is required."
                    />

                    {!!this.context.tenant?.tenantSettings.billingEnabled && (
                        <>
                            {!!isIntercompanyCustomer && (
                                <>
                                    <YesNoButtons
                                        label="Does not require an End Customer"
                                        id="doNotRequireEndCompany"
                                        name="contract.doNotRequireEndCompany"
                                        value={contract.doNotRequireEndCompany}
                                        onClick={this.onYesNoChanged}
                                    />
                                    {!contract.doNotRequireEndCompany && (
                                        <ValidatedSelect
                                            id="endCustomer"
                                            name="contract.endCompanyId"
                                            label="End Customer"
                                            options={customers}
                                            required
                                            value={
                                                (customers ?? []).find(
                                                    (s) => s.value === contract.endCompanyId,
                                                ) ?? ''
                                            }
                                            onChange={this.onSelectChanged}
                                            error="An End customer selection is required."
                                        />
                                    )}
                                </>
                            )}

                            <ValidatedSelect
                                id="billingCompany"
                                name="contract.billingCompanyId"
                                label="Billing Company"
                                options={billingCompanies}
                                value={
                                    (billingCompanies ?? []).find(
                                        (x) => contract.billingCompanyId
                                            === x.value,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                            />

                            <ValidatedSelect
                                id="billingContact"
                                name="contract.billingContactId"
                                label="Billing Contact"
                                options={billingContacts}
                                disabled={!contract?.companyId}
                                value={
                                    (billingContacts ?? []).find(
                                        (x) => contract.billingContactId
                                            === x.value,
                                    ) ?? ''
                                }
                                onChange={this.onContactSelectChanged}
                                showAddButton={contract?.companyId && !contract.billingContactId}
                                onAddButtonClick={() => this.onAddBillingContact()}
                                showEditButton={!!billingContactIsEditable}
                                onEditButtonClick={() => this.onEditBillingContact(contract.billingContactId)}
                                showViewButton={!!billingContactIsEditable}
                                onViewButtonClick={() => this.onEditBillingContact(contract.billingContactId, true)}
                            />

                            <ValidatedSelect
                                id="invoiceTerms"
                                name="contract.invoiceTermsId"
                                label="Payment Terms"
                                options={invoiceTerms}
                                disabled={!contract.companyId}
                                value={
                                    (
                                        invoiceTerms ?? []
                                    ).find(
                                        (x) => contract.invoiceTermsId
                                            === x.value,
                                    ) ?? ''
                                }
                                onChange={
                                    this.onSelectChanged
                                }
                            />

                            <ValidatedSelect
                                id="invoiceMappings"
                                name="contract.invoiceMappingId"
                                label="Invoice Mapping"
                                disabled={!contract.companyId}
                                options={invoiceMappings}
                                value={
                                    (
                                        invoiceMappings
                                        ?? []
                                    ).find(
                                        (x) => contract.invoiceMappingId
                                            === x.value,
                                    ) ?? ''
                                }
                                onChange={
                                    this.onSelectChanged
                                }
                            />

                            {!contract?.id ? (
                                <>
                                    <hr className="m-4" />

                                    <Block className="mt-4 mb-4">
                                        <ValidatedSelect
                                            id="contractTemplateId"
                                            name="contract.contractTemplateId"
                                            label="Template"
                                            formGroupClassName="mb-0"
                                            options={contractTemplates}
                                            hideClear
                                            isLoading={this.state.contractTemplateLoading}
                                            value={
                                                (contractTemplates ?? []).find(
                                                    (x) => contract.contractTemplateId
                                                        === x.value,
                                                ) ?? ''
                                            }
                                            getOptionLabel={(option) => option.key}
                                            getOptionValue={(option) => option.value}
                                            onChange={this.onContractTemplateChanged}
                                            TipComponentProps={{
                                                label: 'Changing the contract template will reset all fields to the template defaults.',
                                                tipText: 'Note',
                                            }}
                                        />
                                    </Block>
                                </>
                            ) : (
                                <ValidatedSelect
                                    id="contractStatusId"
                                    name="contract.contractStatusId"
                                    label="Contract Status"
                                    options={contractStatuses}
                                    required
                                    value={
                                        (contractStatuses ?? []).find(
                                            (x) => contract.contractStatusId
                                                === x.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    error="Contract status selection is required."
                                />

                            )}
                        </>
                    )}

                    {!contract.isProjectContract && !!selfDispatchTimesheetsEnabled && (
                        <>
                            <YesNoButtons
                                label="Is this a Non-Flagging Contract?"
                                id="enableNonFlaggingTimesheets"
                                name="contract.enableNonFlaggingTimesheets"
                                value={contract.enableNonFlaggingTimesheets}
                                onClick={this.onAllowNonFlaggingChanged}
                                disabled={contract.hasJobsOrTimesheets}
                            />

                            {Boolean(contract.hasJobsOrTimesheets) && (
                                <UncontrolledTooltip target="enableNonFlaggingTimesheets">
                                    This option is locked due to this contract containing jobs or timesheets.
                                </UncontrolledTooltip>
                            )}
                        </>
                    )}

                    {!contract.enableNonFlaggingTimesheets && (
                        <>
                            {Boolean(allowRentalChargeTypes) && (
                                <>
                                    <YesNoButtons
                                        id="isProjectContract"
                                        label="Is this a rental contract?"
                                        name="contract.isProjectContract"
                                        onClick={this.onYesNoChanged}
                                        value={contract.isProjectContract}
                                        disabled={isRentalToggleLocked}
                                    />

                                    {Boolean(contract.hasJobsOrTimesheets) && (
                                        <UncontrolledTooltip target="isProjectContract">
                                            This option is locked due to this contract containing jobs or timesheets.
                                        </UncontrolledTooltip>
                                    )}
                                    {!contract.hasJobsOrTimesheets && hasRentalChargeTypes && (
                                        <UncontrolledTooltip target="isProjectContract">
                                            This option is locked because this contract has rental charge types.
                                        </UncontrolledTooltip>
                                    )}

                                    <Collapse isOpen={contract.isProjectContract || combineCharges}>
                                        <ValidatedSelect
                                            id="billingCycle"
                                            name="contract.billingCycleId"
                                            label="Billing Cycle"
                                            required={!!contract.isProjectContract}
                                            options={billingCycles}
                                            value={
                                                (
                                                    billingCycles ?? []
                                                ).find(
                                                    (x) => contract.billingCycleId
                                                        === x.value,
                                                ) ?? ''
                                            }
                                            onChange={
                                                this.onSelectChanged
                                            }
                                            TipComponentProps={!contract.isProjectContract && combineCharges && {
                                                tipText: 'Note',
                                                label: 'Leave this field empty if you do not want the system to automatically generate invoices for this contract.',
                                            }}
                                            error="Billing Cycle selection is required."
                                        />

                                        {contract.billingCycleId == BillingCycles.Weekly ? (
                                            <ValidatedSelect
                                                id="weeklyBillingCycleEndDay"
                                                name="contract.weeklyBillingCycleEndDay"
                                                label="Billing Cycle End Date"
                                                options={DayOfWeekSelect}
                                                value={
                                                    (
                                                        DayOfWeekSelect ?? []
                                                    ).find(
                                                        (x) => contract.weeklyBillingCycleEndDay
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />
                                        ) : contract.billingCycleId == BillingCycles.Monthly && (
                                            <ValidatedSelect
                                                id="monthlyBillingCycleEndDay"
                                                name="contract.monthlyBillingCycleEndDay"
                                                label="Billing Cycle End Date"
                                                options={DayOfMonthSelect}
                                                value={
                                                    (
                                                        DayOfMonthSelect ?? []
                                                    ).find(
                                                        (x) => contract.monthlyBillingCycleEndDay
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />
                                        )}
                                    </Collapse>

                                    {Boolean(contract.isProjectContract) && (
                                        <YesNoButtons
                                            id="isQuickRentalContract"
                                            label="This contract supports quick rentals"
                                            name="contract.isQuickRentalContract"
                                            onClick={this.onYesNoChanged}
                                            value={contract.isQuickRentalContract}
                                            disabled={contract.hasJobsOrTimesheets}
                                        />
                                    )}
                                </>
                            )}

                            <ValidatedSelect
                                id="workOrderTypeId"
                                name="contract.workOrderTypeId"
                                label="Work Order Number?"
                                options={WorkOrderNumberOptions}
                                required
                                value={
                                    WorkOrderNumberOptions.find(
                                        (wt) => contract.workOrderTypeId
                                            === wt.value,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                                error="A work order type selection is required."
                            />

                            <Collapse
                                isOpen={Boolean(contract.workOrderTypeId && contract.workOrderTypeId !== ChargeTypeEligbility.DoNotUse)}
                            >
                                <YesNoButtons
                                    label="Linked Work Orders?"
                                    required={!!contract.workOrderTypeId}
                                    name="contract.allowLinkedWorkOrders"
                                    value={contract.allowLinkedWorkOrders}
                                    onClick={this.onYesNoChanged}
                                />
                            </Collapse>

                            {enableContractDateRange && (
                                <>
                                    <Row>
                                        <Col xs={6}>
                                            <Input
                                                label="Effective Date"
                                                id="effectiveDate"
                                                required
                                                type="date"
                                                name="contract.effectiveDate"
                                                value={contract.effectiveDate ?? ''}
                                                onChange={this.onChange}
                                                error="Effective Date is required."
                                            />
                                        </Col>

                                        <Col xs={6}>
                                            <Input
                                                label="Expiration Date"
                                                id="expirationDate"
                                                required
                                                type="date"
                                                name="contract.expirationDate"
                                                value={contract.expirationDate ?? ''}
                                                onChange={this.onChange}
                                                error="Expiration Date is required."
                                            />
                                        </Col>
                                    </Row>

                                    <ValidatedSelect
                                        id="warningThresholdInDays"
                                        name="contract.warningThresholdInDays"
                                        label="Warning Threshold (Days)"
                                        options={WarningThresholdOptions}
                                        required
                                        value={
                                            (
                                                WarningThresholdOptions
                                                ?? []
                                            ).find(
                                                (x) => contract.warningThresholdInDays
                                                    === x.value,
                                            ) ?? ''
                                        }
                                        onChange={this.onSelectChanged}
                                        error="Warning Threshold is required."
                                        hideClear
                                    />
                                </>
                            )}

                            <NumericInput
                                id="estimatedShiftLengthInHours"
                                label="Estimated Shift Length"
                                name="contract.estimatedShiftLengthInHours"
                                formGroupClassName="w-50"
                                value={contract.estimatedShiftLengthInHours ?? ''}
                                onChange={this.onChange}
                                min={0}
                                step={0.25}
                                preventNegative
                                endAddon={<span className="text-muted">hrs</span>}
                                TipComponentProps={{
                                    label: 'Dispatch will be notified any time a job goes over this duration.',
                                }}
                            />

                            {isInvoicingAllowed && (
                                <>
                                    {glStringsEnabled && (
                                        <YesNoButtons
                                            id="requireGLString"
                                            label="Require GL String"
                                            name="contract.requireGLString"
                                            onClick={this.onYesNoChanged}
                                            value={contract.requireGLString}
                                        />
                                    )}
                                    {autoCalcDriveTimeBillingEnabled && (
                                        <YesNoButtons
                                            id="autoCalculateDriveTimeBilling"
                                            label="Auto-Calculate Drive Time for Billing"
                                            name="contract.autoCalculateDriveTimeBilling"
                                            value={contract.autoCalculateDriveTimeBilling}
                                            onClick={this.onYesNoChanged}
                                        />
                                    )}
                                    {contractsCanEnforceProjectManagers && (
                                        <YesNoButtons
                                            id="requireProjectInformation"
                                            label="Require Project Information when building Jobs?"
                                            name="contract.requireProjectInformation"
                                            onClick={this.onYesNoChanged}
                                            value={contract.requireProjectInformation}
                                        />
                                    )}
                                </>
                            )}

                            {(Boolean(contract.isProjectContract) || tenantSettings.useContractProjects === 2) && (
                                <YesNoButtons
                                    id="requiresProjectForQuickSale"
                                    label="Quick Sale Requires Project"
                                    name="contract.requiresProjectForQuickSale"
                                    onClick={this.onYesNoChanged}
                                    value={contract.requiresProjectForQuickSale}
                                    TipComponentProps={{
                                        label: 'When enabled, all Quick Sales created under this contract must be linked to a specific project.',
                                    }}
                                />
                            )}

                            {enableAutoEscalatingContractRates && (
                                <YesNoButtons
                                    label="Auto-Escalating Rates"
                                    name="contract.autoEscalatingRates"
                                    value={contract.autoEscalatingRates}
                                    onClick={this.onYesNoChanged}
                                />
                            )}

                            {Boolean(tenantSettings.enableCertification) && (
                                <YesNoButtons
                                    label="Jobs may require certifications"
                                    name="contract.certificationRequired"
                                    value={contract.certificationRequired}
                                    onClick={this.onYesNoChanged}
                                />
                            )}

                            <YesNoButtons
                                label="Setup Charges Required"
                                name="contract.setupChargesRequired"
                                value={contract.setupChargesRequired}
                                onClick={this.onYesNoChanged}
                            />

                            <YesNoButtons
                                label="Permit Fees Apply"
                                name="contract.permitFeesApply"
                                value={contract.permitFeesApply}
                                onClick={this.onYesNoChanged}
                            />

                            {timesheetCheckinsEnabled && (
                                <YesNoButtons
                                    label="Require Employee Check-ins"
                                    name="contract.requireEmployeeCheckins"
                                    value={contract.requireEmployeeCheckins}
                                    onClick={this.onYesNoChanged}
                                />
                            )}

                            <YesNoButtons
                                label="Requires Job Approval"
                                name="contract.requiresJobApproval"
                                value={contract.requiresJobApproval}
                                onClick={this.onYesNoChanged}
                            />

                            <YesNoButtons
                                id="requireProjectCompletionReview"
                                label="Require crew lead to report project completion?"
                                name="contract.requireProjectCompletionReview"
                                value={contract.requireProjectCompletionReview}
                                onClick={this.onYesNoChanged}
                                TipComponentProps={{
                                    label: 'When enabled, the crew lead is asked "Was the project completed on this timesheet?" during their timesheet review.',
                                }}
                            />

                            {showWorkCardOnTimesheet && (
                                <ValidatedSelect
                                    id="showWorkCardOnTimesheet"
                                    name="contract.workCardTypeId"
                                    label="Work Card Required?"
                                    options={workCardOptions}
                                    required
                                    value={
                                        workCardOptions.find(
                                            (wc) => contract.workCardTypeId === wc.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    error="A work card type selection is required."
                                />
                            )}

                            {(this.context.checkUserHasPermission(
                                ApplicationPermissions.dispatch_plus_view,
                            ) || this.context.user?.isAdmin) && this.state.operationalCategories?.length > 0 &&
                                (
                                    <ValidatedSelect
                                        id="operationalCategoryId"
                                        name="contract.operationalCategoryId"
                                        label="Operational Category"
                                        options={operationalCategories}
                                        isClearable
                                        value={
                                            operationalCategories.find(
                                                (oc) => contract.operationalCategoryId === oc.value,
                                            ) ?? null
                                        }
                                        onChange={this.onSelectChanged}
                                    />
                                )}

                            {!!this.context.tenant.tenantSettings?.timesheetEmailEnabled && (
                                <div className="mt-4">
                                    <FormGroup>
                                        <FormLabel
                                            className="w-100 contract-email-label"
                                            htmlFor="workOrderTypeId"
                                            label="Timesheet Email Recipients"
                                        />

                                        <TipAlert
                                            label="Email addresses entered below will receive copies of timesheets for this contract."
                                        />

                                        <EmailRecipients
                                            currentEmailAddress={
                                                currentEmailAddress
                                            }
                                            onEmailAdded={this.onEmailAdded}
                                            onEmailChanged={this.onEmailChanged}
                                            onEmailRemoved={this.onEmailRemoved}
                                            emailAddresses={
                                                contract.timesheetEmailRecipients
                                            }
                                        />
                                    </FormGroup>
                                </div>
                            )}
                        </>
                    )}

                    {enablePrevailingWageContracts && (
                        <YesNoButtons
                            label="Prevailing Wage"
                            name="contract.prevailingWage"
                            value={contract.prevailingWage}
                            onClick={this.onYesNoChanged}
                        />
                    )}

                    <Collapse isOpen={contract.prevailingWage || isUnionWorkEnforced}>
                        <YesNoButtons
                            label="Require Certified Payroll Report"
                            name="contract.requireCertifiedPayrollReport"
                            value={contract.requireCertifiedPayrollReport}
                            onClick={this.onYesNoChanged}
                        />
                        <Collapse isOpen={contract.requireCertifiedPayrollReport}>
                            <Input
                                label="Certified Payroll Project & Location"
                                id="certifiedPayrollLocation"
                                name="contract.certifiedPayrollLocation"
                                value={contract.certifiedPayrollLocation ?? ''}
                                onChange={this.onChange}
                            />
                            <ValidatedSelect
                                id="certifiedPayrollFormId"
                                name="contract.certifiedPayrollFormId"
                                label="Certified Payroll Form"
                                options={cpForms}
                                required={(contract.prevailingWage || isUnionWorkEnforced) && contract.requireCertifiedPayrollReport}
                                value={
                                    (cpForms || []).find(
                                        (x) => contract.certifiedPayrollFormId === x.value,
                                    ) ?? ''
                                }
                                onChange={this.onCertifiedPayrollFormChanged}
                                error="A certified payroll format selection is required."
                            />
                            <YesNoButtons
                                label="Map full SSN"
                                name="contract.requireFullSSN"
                                value={contract.requireFullSSN}
                                onClick={this.onYesNoChanged}
                            />
                        </Collapse>
                    </Collapse>

                    {enablePrevailingWageContracts && (
                        <>
                            <Collapse isOpen={!contract.prevailingWage}>
                                <YesNoButtons
                                    label="Require a pay schedule for this contract?"
                                    name="contract.requirePaySchedule"
                                    value={contract.requirePaySchedule}
                                    onClick={this.onYesNoChanged}
                                />
                            </Collapse>

                            <Collapse isOpen={(contract.prevailingWage || contract.requirePaySchedule) && !!contract.newPaySchedule?.governingBody}>
                                <TipAlert
                                    tipText="Note"
                                    label={`Pay Schedule ${contract.newPaySchedule?.governingBody} will be added to the contract.`}
                                />
                            </Collapse>
                        </>
                    )}

                    {isUnionWorkEnforced && !contract.enableNonFlaggingTimesheets ? (
                        <>
                            <SubHeading>Union Settings</SubHeading>
                            <div className="mt-4">
                                <ValidatedSelect
                                    id="contract.usStates"
                                    name="contract.usStates"
                                    label="Contract is valid in the following state(s)"
                                    isMulti
                                    required
                                    options={usStates}
                                    value={contract.usStates ?? ''}
                                    getOptionLabel={(option) => option.label ?? option.name}
                                    getOptionValue={(option) => option.id ?? option.value}
                                    onChange={this.onContractStateChanged}
                                    error="At least one state is required."
                                />

                                <ValidatedSelect
                                    id="contractUnionWorkTypeId"
                                    name="contract.contractUnionWorkTypeId"
                                    label="Union Work Type"
                                    options={contractUnionWorkTypes}
                                    required
                                    error="Please select a work type for this contract"
                                    value={selectedWorkType}
                                    onChange={
                                        this.onSelectChanged
                                    }
                                />

                                <ValidatedSelect
                                    id="contractScheduleTypeId"
                                    name="contract.contractScheduleTypeId"
                                    label="Pay Schedule"
                                    options={contractScheduleTypes}
                                    required={selectedWorkType?.isUnionWork === true}
                                    error="A pay schedule selection is required"
                                    value={(
                                        contractScheduleTypes ?? []
                                    ).find(
                                        (x) => contract.contractScheduleTypeId === x.value,
                                    ) ?? ''}
                                    onChange={
                                        this.onSelectChanged
                                    }
                                />

                                <Collapse isOpen={selectedWorkType?.isUnionWork === true}>
                                    {Boolean(doubleTimeEnabled) && (
                                        <YesNoButtons
                                            label="Sunday Has Double Time Pay?"
                                            name="contract.hasSundayDT"
                                            required
                                            value={contract.hasSundayDT}
                                            onClick={this.onYesNoChanged}
                                        />
                                    )}

                                    <YesNoButtons
                                        label="Does this contract use a different billing schedule than above?"
                                        required
                                        name="contract.billingHasSeparateContractSchedule"
                                        value={contract.billingHasSeparateContractSchedule}
                                        onClick={this.onYesNoChanged}
                                    />

                                    <Collapse isOpen={Boolean(contract.billingHasSeparateContractSchedule)}>
                                        <ValidatedSelect
                                            id="billingContractScheduleTypeId"
                                            name="contract.billingContractScheduleTypeId"
                                            label="Billing Schedule"
                                            options={contractScheduleTypes}
                                            required={Boolean(contract.billingHasSeparateContractSchedule)}
                                            error="A billing schedule selection is required"
                                            value={(
                                                contractScheduleTypes ?? []
                                            ).find(
                                                (x) => contract.billingContractScheduleTypeId === x.value,
                                            ) ?? ''}
                                            onChange={
                                                this.onSelectChanged
                                            }
                                        />

                                        <Collapse
                                            isOpen={(
                                                contractScheduleTypes ?? []
                                            ).find(
                                                (x) => contract.billingContractScheduleTypeId === x.value,
                                            )?.days === 0}
                                        >
                                            <YesNoButtons
                                                label="Disable OT/DT Billing for Sunday?"
                                                name="contract.hasSundayRegularBilling"
                                                value={contract.hasSundayRegularBilling}
                                                onClick={this.onYesNoChanged}
                                                required
                                            />
                                        </Collapse>
                                    </Collapse>

                                    <YesNoButtons
                                        label="Does this contract use billing bump rules other than 2-4-8?"
                                        required
                                        name="contract.billingHasCustomBumpRules"
                                        value={contract.billingHasCustomBumpRules}
                                        onClick={this.onYesNoChanged}
                                    />

                                    <Collapse isOpen={Boolean(contract.billingHasCustomBumpRules)}>
                                        <TipAlert
                                            label="Leaving this section empty will disable billing bump rules for this contract."
                                            containerClassName="mt-4"
                                        />

                                        <Card className="mt-2">
                                            <ContractBumpRuleGrid
                                                bumpRules={contract.bumpRules}
                                                onBumpRulesChange={(updatedBumpRules) => {
                                                    contract.bumpRules = updatedBumpRules;

                                                    this.setState({ contract });
                                                }}
                                            />
                                        </Card>
                                    </Collapse>
                                </Collapse>
                            </div>
                        </>
                    ) : (
                        <>
                            {(Boolean(doubleTimeEnabled) || Boolean(contract.companyUsesContractSchedule)) && (
                                <>
                                    <SubHeading>Billing/Payroll Settings</SubHeading>
                                    <div className="mt-4">
                                        {Boolean(doubleTimeEnabled) && (
                                            <YesNoButtons
                                                label="Sunday Has Double Time Pay?"
                                                name="contract.hasSundayDT"
                                                value={contract.hasSundayDT}
                                                onClick={this.onYesNoChanged}
                                            />
                                        )}

                                        {(Boolean(contract.companyUsesContractSchedule) || isUnionWorkEnforced) && (
                                            <ValidatedSelect
                                                id="contractScheduleTypeId"
                                                name="contract.contractScheduleTypeId"
                                                label={contract.companyUsesContractSchedule ? 'Billing Schedule' : 'Pay Schedule'}
                                                options={contractScheduleTypes}
                                                required={Boolean(contract.companyUsesContractSchedule)}
                                                error="A billing schedule selection is required"
                                                value={(
                                                    contractScheduleTypes ?? []
                                                ).find(
                                                    (x) => contract.contractScheduleTypeId === x.value,
                                                ) ?? ''}
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {isBillingPerJobTypeAllowed && !contract.enableNonFlaggingTimesheets && (
                        <>
                            <SubHeading>Job Type Settings</SubHeading>
                            <div className="mt-4">
                                <ValidatedSelect
                                    id="contract.contractJobTypes"
                                    name="contract.contractJobTypes"
                                    label="Job Types with Custom Billing Rates"
                                    isMulti
                                    options={jobTypes}
                                    disabled={selectedWorkType?.isUnionWork === true}
                                    value={(
                                        jobTypes ?? []
                                    ).filter(
                                        (j) => (!(selectedWorkType?.isUnionWork && contract.usStates?.length) ?
                                            contract.contractJobTypes ?? [] : [])
                                            .some(
                                                (x) => x.jobTypeId === j.value,
                                            ),
                                    )}
                                    onChange={this.onContractJobTypeChanged}
                                />

                                {selectedWorkType?.isUnionWork === true ? (
                                    <TipAlert
                                        tipText="Note"
                                        label="Custom billing rates per job type are disabled for Union Work contracts."
                                    />
                                ) : (
                                    <TipAlert
                                        label="Select the job types that require custom billing rates for this contract."
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {!!contract.id && !isUnionWorkEnforced && (contract.prevailingWage || contract.requirePaySchedule) && (
                        <>
                            {!tenantSettings.enablePrevailingWageCountyGroups ? (
                                <>
                                    <SubHeading>Pay Schedules</SubHeading>
                                    <SimpleTable
                                        className="mb-0"
                                        id="contractPrevailingWageTable"
                                        onAddFunction={this.onAddPaySchedules}
                                        addLabelText={
                                            this.context.formIsOpen ? (
                                                <span>
                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                    Opening pay schedules form...
                                                </span>
                                            ) : (
                                                'Add Pay Schedule'
                                            )
                                        }
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={[
                                            {
                                                name: 'Description',
                                                class: 'ct-job-number',
                                            },
                                            {
                                                name: 'State',
                                                class: 'ct-job-name',
                                            },
                                            {
                                                name: 'Counties',
                                                class: 'ct-job-week',
                                            },
                                            {
                                                name: 'Reg',
                                                class: 'ct-job-active',
                                            },
                                            {
                                                name: 'OT',
                                                class: 'ct-job-active',
                                            },
                                            {
                                                name: 'Fringe',
                                                class: 'ct-job-active',
                                            },
                                            {
                                                name: '',
                                                class: 'ct-job-active',
                                            },
                                        ]}
                                        editable={false}
                                        entities={contract.contractPaySchedules}
                                        rowRenderer={
                                            this.renderContractPaySchedules
                                        }
                                        noDataText="No pay schedules found."
                                    />
                                </>
                            ) : (
                                <>
                                    <SubHeading>County Groups</SubHeading>
                                    <SimpleTable
                                        className="mb-0"
                                        id="contractPrevailingWageCountyGroupsTable"
                                        onAddFunction={this.onAddCountyGroups}
                                        addLabelText={
                                            this.context.formIsOpen ? (
                                                <span>
                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                    Opening county groups form...
                                                </span>
                                            ) : (
                                                'Add County Group'
                                            )
                                        }
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={[
                                            {
                                                name: 'Description',
                                                class: 'ct-job-number',
                                            },
                                            {
                                                name: 'State',
                                                class: 'ct-job-name',
                                            },
                                            {
                                                name: 'Counties',
                                                class: 'ct-job-week',
                                            },
                                            {
                                                name: '',
                                                class: 'ct-job-active',
                                            },
                                        ]}
                                        editable={false}
                                        entities={contract.contractUSCountyGroups}
                                        rowRenderer={
                                            this.renderUSContractCountyGroups
                                        }
                                        noDataText="No county groups found."
                                    />
                                </>
                            )}
                            <FormGroup className="flex-row-reverse">
                                <FormCheckbox
                                    className="m-3"
                                    id="contract.prevailingWageAllowEmployeePayRate"
                                    name="contract.prevailingWageAllowEmployeePayRate"
                                    checked={!!contract.prevailingWageAllowEmployeePayRate}
                                    onChange={this.onChange}
                                    labelText="Use the employee pay rate if it exceeds the Pay Schedule rate"
                                />
                            </FormGroup>

                            {Boolean(contract.prevailingWage) && !tenantSettings.quirksEnabled && (
                                <Container>
                                    <SubHeading>Pay Incentives</SubHeading>
                                    <ContractPayrollIncentiveGrid
                                        incentives={contract.payrollIncentives}
                                        onIncentivesChange={this.handleIncentivesChange}
                                        userGroups={this.state.employeeGroups}
                                    />
                                </Container>
                            )}
                        </>
                    )}

                    <p />

                    {!!contract.createdFromId && !!(contract?.contractNewChargeTypes ?? []).length && (
                        <div className="mb-3">
                            <SubHeading>New Charge Types</SubHeading>
                            <Card className="mt-2">
                                <EstimateChargeTypeGrid
                                    chargeTypes={contract.contractNewChargeTypes}
                                    resourceTypes={resourceTypes}
                                    equipmentTypes={equipmentTypes}
                                    rentalUnitTypes={rentalUnitTypes}
                                    resGroups={resGroups}
                                    isProjectContract={!!contract?.isProjectContract}
                                />
                            </Card>
                        </div>
                    )}

                    {!!contract.id && (
                        <>
                            <SubHeading>Charge Types</SubHeading>
                            <SimpleTable
                                className="mb-0"
                                id="contractChargeTypesTable"
                                onAddFunction={this.onAddChargeTypes}
                                addButtonEnabled={
                                    parseInt(
                                        this.state.contract.companyId
                                        ?? 0,
                                    ) > 0
                                }
                                addLabelText={
                                    this.context.formIsOpen ? (
                                        <span>
                                            <i className="fa fa-spin fa-circle-notch mr-1" />
                                            Opening charge types form...
                                        </span>
                                    ) : (
                                        'Add Charge Types'
                                    )
                                }
                                onRefreshFunction={this.state.contract.createdFromId ? this.onRefreshChargeTypes : null}
                                refreshButtonEnabled={
                                    parseInt(
                                        this.state.contract.companyId
                                        ?? 0,
                                    ) > 0
                                }
                                refreshLabelText={
                                    this.context.formIsOpen ? (
                                        <span>
                                            <i className="fa fa-spin fa-circle-notch mr-1" />
                                            Pulling charge types updates...
                                        </span>
                                    ) : (
                                        'Refresh Charge Types'
                                    )
                                }
                                leftToolbarContent={
                                    !!(
                                        (this.state.contract ?? {})
                                            .contractChargeTypes ?? []
                                    ).length && (
                                        <SmallButton
                                            type="button"
                                            disabled={
                                                !!this.context
                                                    .formIsOpen
                                            }
                                            onClick={
                                                this
                                                    .onReorderChargeTypes
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faSort}
                                                className="mr-2"
                                            />
                                            Reorder
                                        </SmallButton>
                                    )
                                }
                                noTopBorder
                                permission="contract"
                                tableHeaderLabels={
                                    chargeTypeHeaderLabels
                                }
                                editable={false}
                                entities={contract.contractChargeTypes}
                                rowRenderer={
                                    this.renderContractChargeTypesRows
                                }
                                noDataText="No charge types found."
                            />

                            {!!(contract?.contractChargeTypes ?? []).length && (
                                <FlexEndRow className="pt-1 pl-3 pr-4">
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="site-link text-right"
                                        onClick={() => {
                                            const { contract } = {
                                                ...this.state,
                                            };
                                            const {
                                                contractChargeTypes,
                                            } = { ...contract };
                                            const updated = [
                                                ...contractChargeTypes.map(
                                                    (x) => ({
                                                        ...x,
                                                        chargeTypeEligibilityId:
                                                            _ContractChargeTypeEligibility.Required,
                                                    }),
                                                ),
                                            ];
                                            contract.contractChargeTypes = updated;
                                            this.setState({
                                                contract: {
                                                    ...contract,
                                                },
                                            });
                                        }}
                                    >
                                        Require All Charge Types
                                    </Button>
                                </FlexEndRow>
                            )}

                            {isCertificationsAllowed && (
                                <div className="mt-3">
                                    <SubHeading>Certifications</SubHeading>

                                    <SimpleTable
                                        id="certificationsTable"
                                        entities={contract.contractCertifications}
                                        noTopBorder
                                        permission="contract"
                                        addLabelText={this.context.formIsOpen ? (
                                            <span>
                                                <i className="fa fa-spin fa-circle-notch mr-1" />
                                                Opening certifications form
                                            </span>
                                        ) : 'Add Certifications'}
                                        onAddFunction={this.toggleCertificationModal}
                                        editable={false}
                                        tableHeaderLabels={[
                                            { name: 'Certification', class: 'text-left' },
                                            { name: 'Abbreviation', class: 'text-left' },
                                            { name: '', class: 'text-center' },
                                        ]}
                                        rowRenderer={() =>
                                            (contract.contractCertifications ?? []).map((c, i) => (
                                                <tr key={i}>
                                                    <td>{c.certificationName}</td>
                                                    <td>{c.certificationAbbreviation || c.abbreviation}</td>
                                                    <td className="text-center">
                                                        <IconButton
                                                            size="sm"
                                                            onClick={() => this.onRemoveCertification(i)}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                            />
                                                        </IconButton>
                                                    </td>
                                                </tr>
                                            ))}
                                        noDataText="No certifications on this contract."
                                    />
                                </div>
                            )}

                            {isChargeTypeGroupsAllowed && (
                                <div className="mt-3 mb-3">
                                    <SubHeading>Charge Type Groups</SubHeading>
                                    <SimpleTable
                                        className="mb-0"
                                        onAddFunction={this.onAddChargeTypeGroups}
                                        addButtonEnabled={
                                            parseInt(
                                                this.state.contract.companyId
                                                ?? 0,
                                            ) > 0
                                        }
                                        addLabelText={
                                            this.context.formIsOpen ? (
                                                <span>
                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                    Opening charge type group form...
                                                </span>
                                            ) : (
                                                'Add Charge Type Groups'
                                            )
                                        }
                                        refreshButtonEnabled={false}
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={
                                            chargeTypeGroupHeaderLabels
                                        }
                                        editable={false}
                                        entities={contract.contractChargeTypeGroups}
                                        rowRenderer={
                                            this.renderContractChargeTypeGroupsRows
                                        }
                                        noDataText="No charge type groups found."
                                    />
                                </div>
                            )}

                            {!showSelfDispatchOptions && this.renderJobAndProjectSections({
                                contractJobs,
                                hasContractJobs,
                                projectsEnabledForContract,
                                hasJobRelatedCustomForms,
                            })}
                        </>
                    )}

                    <FlexCenterRow className="mb-3">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="contractForm"
                        >
                            <FontAwesomeIcon
                                className="mr-1"
                                icon={faSave}
                            />
                            {existing ? 'Save' : 'Save New Contract'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>
                {Boolean(contract.id) && (
                    <ContractHistoryForm
                        show={this.state.showContractHistory}
                        contractId={contract.id}
                        onClose={this.onCloseContractHistory}
                    />
                )}
                <ChargeTypeSelect
                    ref={this.chargeTypeSelectorRef}
                    id="chargeTypeSelector"
                    onAddCallback={this.onAddChargeTypesCallback}
                />

                <ChargeTypeGroupSelect
                    ref={this.chargeTypeGroupSelectorRef}
                    id="chargeTypeGroupSelector"
                    onAddCallback={this.onAddChargeTypeGroupsCallback}
                />

                <OrganizationContactForm
                    ref={this.organizationContactFormRef}
                    show={this.state.showContactsForm}
                    contact={this.state.selectedContact}
                    readOnly={this.state.contactFormIsReadOnly}
                    allowContactTypeEdit={!billingContactIsEditable}
                    allowAllContactTypes
                    id="organizationContactForm"
                    onClose={() => {
                        this.setState({ showContactsForm: false, contactFormIsReadOnly: false });
                        this.organizationContactFormRef.current.resetForm();
                        this.setState({ selectedContact: null });
                    }}
                    onChange={(e) => {
                        const { selectedContact } = this.state;
                        selectedContact[e.target.name] = e.target.value;
                        this.setState({ selectedContact });
                    }}
                    onCheckedChanged={(e) => {
                        const { selectedContact } = this.state;
                        selectedContact[e.target.name] = e.target.checked;
                        this.setState({ selectedContact });
                    }}
                    onContactTypeChanged={(items) => {
                        const { selectedContact } = this.state;
                        selectedContact.contactTypes = (items || []).map((x) => x.value);
                        this.setState({ selectedContact });
                    }}
                    onCompanyChanged={(item) => {
                        const { selectedContact } = this.state;
                        const id = (item ?? {}).value ?? '';
                        selectedContact.companyId = id;
                        this.setState({ selectedContact });
                    }}
                    onSaveCallback={this.onOrganizationContactSaved}
                />

                <PayScheduleSelect
                    ref={this.payScheduleSelectorRef}
                    id="payScheduleSelector"
                    onAddCallback={this.onAddPayScheduleCallback}
                />

                <USCountyGroupSelect
                    ref={this.uSCountyGroupSelectorRef}
                    id="uSCountyGroupSelector"
                    onAddCallback={this.onAddUSCountyGroupCallback}
                />

                {!!isBillingRatesEnabled && (
                    <BillingRateForm
                        ref={this.rateFormRef}
                        id="billingRateForm"
                        entity={this.state.selectedRate}
                        entityName="selectedRate"
                        show={this.state.showRateForm}
                        onClose={this.onRateFormClosed}
                        onSaveCallback={this.onRateSaved}
                        onRemoveCallback={this.onRateRemoved}
                        onChange={this.onChange}
                        onSelectChange={this.onSelectChanged}
                        chargeTypes={chargeTypes}
                        chargeTypeGroups={chargeTypeGroups}
                        showRemove={showRateFormDelete}
                        showDoubleTime={contract.companyEnablesBillingDT || selectedWorkType?.isUnionWork}
                        showShiftDifferential={contract.companyHasShiftDifferentialBilling}
                        showChargeType
                        chargeTypeReadonly
                    />
                )}

                <SlideForm
                    loading={false}
                    show={this.state.showReorderWidget}
                    id="reorderChargeTypesWidget"
                    formIcon={faFileContract}
                    formTitle="Reorder Charge Types"
                    ref={this.chargeTypesWidgetRef}
                    onClose={() => {
                        this.setState(
                            (state) => (
                                (state.showReorderWidget = false), state
                            ),
                        );
                    }}
                >
                    <ReorderListWidget
                        entities={this.state.selectedSortableChargeTypes}
                        entityName="description"
                        customColors={false}
                        setUpdatedEntities={(items) => {
                            this.setState({
                                selectedSortableChargeTypes: items,
                            });
                        }}
                        ref={this.chargeTypesWidgetRef}
                        onSaveCallback={() => {
                            const { contract, selectedSortableChargeTypes } = {
                                ...this.state,
                            };
                            let updated = [...selectedSortableChargeTypes];
                            updated = updated.map((x, index) => ({ ...x, ...{ order: (index += 1) } }));
                            contract.contractChargeTypes = updated;
                            this.setState({
                                contract,
                                showReorderWidget: false,
                            });
                        }}
                    />
                </SlideForm>
            </>
        );
    }
}
export default ContractForm;
